import { Link, useParams } from "react-router-dom";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import ReactMarkdown from "react-markdown";
import { CaseSummaryCard } from "../components/CaseView/CaseSummaryCard";
import { CallScriptsCard } from "../components/CallScriptsCard";
import CallShieldToggle from "../components/CallShieldToggle";
import { PriorAuthLetterPanel } from "../components/PriorAuthLetterPanel";
import { ICD10CodeLookup } from "../components/ICD10CodeLookup";
import { SubmissionChecklist } from "../components/SubmissionChecklist";
import { normalizeInsurer } from "../utils/submissionValidation";
import { DenialCaptureModal } from "../components/CaseView/DenialCaptureModal";

// Import from modular components
import {
  IconChevronDown,
  IconChevron,
  IconCheck,
  IconCopy,
  IconPencil,
  IconShield,
  IconLock,
  IconDocument,
  RebuttalLibrarySection,
  CallTranscription,
} from "../components";

// Import the call nudge system
import { useCallNudge } from "../hooks/useCallNudge";
import { useSubscription } from "../hooks/useSubscription";
import { CallNudgeOverlay } from "../components/CaseView/CallNudgeOverlay";
import { CallQuickTap } from "../components/CaseView/CallQuickTap";

// Import medication utilities
import {
  analyzeMedications,
  generateMedicationSummaryDisplay,
  getMedicationBasedRebuttals,
  getStepTherapyDocumentation,
  MedicationSummary,
} from "../data/medications";

// Import extracted types
import type {
  SectionKey,
  CollapsibleSectionProps,
  SectionControlsProps,
  Case,
  SiteOfCareInfo,
  AdaptedCase,
  ParsedSection,
  ObjectionRebuttal,
  FontSize,
  MedicationEntry,
} from "../types/caseView";

// Import TimelineBuilder
import { TimelineBuilder } from "../components/TimelineBuilder";

import {
  buildTalkingPointCategories,
  buildSpeakableSummary,
  classifyCase,
  getAutoSelectedPills,
  type TalkingPointCategory,
  type TalkingPointPill,
  type TalkingPointExtra,
  type CaseClassification,
} from "../components/PrepGuide/talkingPointsEngine";
import { fetchAITalkingPointsCached } from "../components/PrepGuide/aiTalkingPoints";
import type { TimelineOutput } from "../components/TimelineBuilder/types";

// Import guideline resolver
import { parseAndResolveEvidence } from "../utils/guidelineResolver";

// Import extracted helpers
import {
  getSectionKey,
  DEFAULT_EXPANDED_SECTIONS,
  ALL_SECTION_KEYS,
  getStorageKey,
  loadExpandedState,
  saveExpandedState,
  FACILITY_TYPES,
  HOSPITAL_JUSTIFICATION_FACTORS,
  toTitleCase,
  parseSiteOfCareInfo,
  isSiteOfCareDenial,
  extractMedicationFromCase,
  parsePriorMedications,
  parseAgeRange,
  parseTriedTreatments,
  adaptCaseForComponents,
  parseObjectionsAndRebuttals,
  parseObjectionsFromSeparateSections,
  getIconTypeForSection,
  parseAIPrep,
  getFontSizeClasses,
} from "../utils/caseViewHelpers";

// Import extracted components
import { CollapsibleSection } from "../components/CaseView/CollapsibleSection";
import { SectionControls } from "../components/CaseView/SectionControls";
import {
  SectionIcon,
  IconClipboard,
  IconExclamationTriangle,
  IconBook,
  IconBolt,
} from "../components/CaseView/SectionIcon";
import { CopyButton } from "../components/CaseView/CopyButton";
import { PanicButton } from "../components/CaseView/PanicButton";
import { DecisionTreeRebuttals } from "../components/CaseView/DecisionTreeRebuttals";
import { SiteOfCareDisplay } from "../components/SiteOfCare/SiteOfCareDisplay";
import { PostCallRecap } from "../components/CaseView/PostCallRecap";
import { ReviewerAuthorityStrip } from "../components/CaseView/ReviewerAuthorityStrip";
import type { ReviewerAuthority } from "../components/CaseView/ReviewerAuthorityStrip";
import { getCaseDisplayName } from "../utils/caseDisplayName";

// NOTE: RebuttalSidebar is no longer needed - rebuttals display inline in CallTranscription

// ============================================
// MEDICATION DISPLAY COMPONENTS
// ============================================

interface MedicationDisplayProps {
  medications: MedicationEntry[];
  showDetails?: boolean;
}

function MedicationDisplay({
  medications,
  showDetails = false,
}: MedicationDisplayProps) {
  if (medications.length === 0) {
    return (
      <span className="text-gray-400 text-sm">
        No prior medications documented
      </span>
    );
  }

  // Group by class
  const summaries = analyzeMedications(medications.map((m) => m.name));

  if (!showDetails) {
    // Compact view: "NSAIDs (2), SSRI (1), ..."
    return (
      <span className="text-gray-700 text-sm">
        {summaries.map((s) => `${s.class_name} (${s.count})`).join(", ")}
      </span>
    );
  }

  // Detailed view with step therapy levels
  const stepDoc = getStepTherapyDocumentation(medications.map((m) => m.name));

  return (
    <div className="space-y-3">
      {stepDoc.firstLine.length > 0 && (
        <div>
          <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
            First-Line Therapy
          </span>
          <div className="mt-1 text-sm text-gray-700">
            {stepDoc.firstLine.map((s) => (
              <div key={s.class_id} className="flex items-center gap-2">
                <span className="font-medium">{s.class_name}</span>
                <span className="text-gray-500">
                  ({s.medications.join(", ")})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stepDoc.secondLine.length > 0 && (
        <div>
          <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
            Second-Line Therapy
          </span>
          <div className="mt-1 text-sm text-gray-700">
            {stepDoc.secondLine.map((s) => (
              <div key={s.class_id} className="flex items-center gap-2">
                <span className="font-medium">{s.class_name}</span>
                <span className="text-gray-500">
                  ({s.medications.join(", ")})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stepDoc.thirdLine.length > 0 && (
        <div>
          <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
            Specialty/Advanced Therapy
          </span>
          <div className="mt-1 text-sm text-gray-700">
            {stepDoc.thirdLine.map((s) => (
              <div key={s.class_id} className="flex items-center gap-2">
                <span className="font-medium">{s.class_name}</span>
                <span className="text-gray-500">
                  ({s.medications.join(", ")})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface MedicationPanelProps {
  caseData: Case;
  callMode?: boolean;
}

function MedicationPanel({ caseData, callMode = false }: MedicationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedRebuttals, setCopiedRebuttals] = useState(false);
  const medications = parsePriorMedications(caseData);

  if (medications.length === 0) return null;

  const rebuttals = getMedicationBasedRebuttals(medications.map((m) => m.name));

  const handleCopyRebuttals = async () => {
    const text = rebuttals.join("\n\n");
    await navigator.clipboard.writeText(text);
    setCopiedRebuttals(true);
    setTimeout(() => setCopiedRebuttals(false), 2000);
  };

  return (
    <div
      className={`rounded-lg overflow-hidden ${
        callMode
          ? "bg-slate-800 border border-slate-700"
          : "bg-white border border-gray-200"
      }`}
    >
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
          callMode ? "hover:bg-slate-700" : "hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-5 h-5 ${
              callMode ? "text-teal-400" : "text-teal-600"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
            />
          </svg>
          <span
            className={`font-medium ${
              callMode ? "text-white" : "text-gray-900"
            }`}
          >
            Prior Medications
          </span>
          <span
            className={`text-sm ${
              callMode ? "text-slate-400" : "text-gray-500"
            }`}
          >
            ({medications.length})
          </span>
        </div>
        <svg
          className={`w-5 h-5 transition-transform ${
            isExpanded ? "rotate-180" : ""
          } ${callMode ? "text-slate-400" : "text-gray-400"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Summary - always visible */}
      <div
        className={`px-4 pb-3 text-sm ${
          callMode ? "text-slate-300" : "text-gray-600"
        }`}
      >
        {generateMedicationSummaryDisplay(medications.map((m) => m.name))}
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div
          className={`px-4 pb-4 border-t ${
            callMode ? "border-slate-700" : "border-gray-100"
          }`}
        >
          {/* Detailed medication list */}
          <div className="mt-3">
            <MedicationDisplay medications={medications} showDetails={true} />
          </div>

          {/* Auto-generated rebuttals */}
          {rebuttals.length > 0 && (
            <div
              className={`mt-4 rounded-lg p-3 ${
                callMode
                  ? "bg-teal-900/30 border border-teal-700/50"
                  : "bg-teal-50 border border-teal-100"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4
                  className={`text-sm font-medium ${
                    callMode ? "text-teal-300" : "text-teal-800"
                  }`}
                >
                  Ready-to-Use Arguments
                </h4>
                <button
                  onClick={handleCopyRebuttals}
                  className={`text-xs flex items-center gap-1 transition-colors ${
                    callMode
                      ? "text-teal-400 hover:text-teal-300"
                      : "text-teal-600 hover:text-teal-800"
                  }`}
                >
                  {copiedRebuttals ? (
                    <>
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      Copy all
                    </>
                  )}
                </button>
              </div>
              <ul className="space-y-2">
                {rebuttals.map((rebuttal, i) => (
                  <li
                    key={i}
                    className={`text-sm rounded px-2 py-1.5 ${
                      callMode
                        ? "text-teal-100 bg-slate-800/50 border border-teal-800/30"
                        : "text-teal-900 bg-white border border-teal-100"
                    }`}
                  >
                    "{rebuttal}"
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// ICONS (kept locally as they're used by internal components)
// ============================================

function IconMinus({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
    </svg>
  );
}

function IconPlus({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.5v15m7.5-7.5h-15"
      />
    </svg>
  );
}

function IconArrowRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
      />
    </svg>
  );
}

function IconArrowLeft({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
      />
    </svg>
  );
}

function IconRefresh({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function IconStarOutline({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.562.562 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
      />
    </svg>
  );
}

function IconStarFilled({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function IconTimeline({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

// ============================================
// UTILITY FUNCTIONS (module level)
// ============================================

// Determine timeline case type from request type
function getTimelineCaseType(requestType: string | null | undefined): string {
  const rt = (requestType || "").toLowerCase();
  if (
    rt.includes("mri") ||
    rt.includes("ct") ||
    rt.includes("imaging") ||
    rt.includes("x-ray")
  )
    return "IMAGING";
  if (rt.includes("surgery") || rt.includes("surgical")) return "SURGERY";
  if (rt.includes("procedure") || rt.includes("injection")) return "PROCEDURE";
  if (rt.includes("medication") || rt.includes("drug") || rt.includes("rx"))
    return "MEDICATION";
  if (
    rt.includes("admission") ||
    rt.includes("inpatient") ||
    rt.includes("level of care")
  )
    return "LEVEL_OF_CARE";
  if (
    rt.includes("dme") ||
    rt.includes("equipment") ||
    rt.includes("wheelchair")
  )
    return "DME";
  if (rt.includes("therapy") || rt.includes("pt") || rt.includes("ot"))
    return "THERAPY";
  return "OTHER";
}

// ============================================
// DOCUMENT CHECKLIST PANEL - Dynamic Prep Assistant
// ============================================

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  category: "clinical" | "documentation" | "appeals" | "insurer";
  required: boolean;
}

interface ChecklistGroup {
  title: string;
  items: ChecklistItem[];
}

// Generate dynamic checklist based on case data
function generateDynamicChecklist(caseData: AdaptedCase): ChecklistGroup[] {
  const groups: ChecklistGroup[] = [];
  const requestType = caseData.request_type?.toLowerCase() || "";
  const denialReason = caseData.denial_reason?.toLowerCase() || "";
  const diagnoses = caseData.diagnoses?.toLowerCase() || "";
  const insurer = caseData.insurance_company?.toLowerCase() || "";

  // Clinical Documentation Group
  const clinicalItems: ChecklistItem[] = [];

  // Always needed
  clinicalItems.push({
    id: "diagnosis-confirmed",
    label: "Diagnosis confirmed",
    description: `ICD-10 codes documented for ${
      caseData.diagnoses || "condition"
    }`,
    category: "clinical",
    required: true,
  });

  // Request-type specific items
  if (requestType.includes("medication") || requestType.includes("drug")) {
    clinicalItems.push({
      id: "med-indication",
      label: "Indication documented",
      description: "Clinical rationale for this specific medication",
      category: "clinical",
      required: true,
    });
    clinicalItems.push({
      id: "dosing-appropriate",
      label: "Dosing verified",
      description: "Dose, frequency, and duration match guidelines",
      category: "clinical",
      required: true,
    });
  }

  if (
    requestType.includes("imaging") ||
    requestType.includes("mri") ||
    requestType.includes("ct")
  ) {
    clinicalItems.push({
      id: "clinical-indication",
      label: "Clinical indication",
      description: "Symptoms or findings warranting imaging",
      category: "clinical",
      required: true,
    });
    clinicalItems.push({
      id: "prior-imaging",
      label: "Prior imaging reviewed",
      description: "Previous studies referenced if applicable",
      category: "clinical",
      required: false,
    });
  }

  if (requestType.includes("procedure") || requestType.includes("surgery")) {
    clinicalItems.push({
      id: "conservative-tx",
      label: "Conservative treatment documented",
      description: "Non-surgical approaches attempted",
      category: "clinical",
      required: true,
    });
    clinicalItems.push({
      id: "surgical-necessity",
      label: "Surgical necessity",
      description: "Why procedure is needed now",
      category: "clinical",
      required: true,
    });
  }

  // Add functional status for pain/musculoskeletal
  if (
    diagnoses.includes("pain") ||
    diagnoses.includes("spine") ||
    diagnoses.includes("back") ||
    diagnoses.includes("knee") ||
    diagnoses.includes("shoulder") ||
    diagnoses.includes("hip")
  ) {
    clinicalItems.push({
      id: "functional-status",
      label: "Functional impact",
      description: "ADLs, work limitations, quality of life",
      category: "clinical",
      required: true,
    });
  }

  // Site of care specific items
  if (
    denialReason.includes("site of care") ||
    denialReason.includes("facility")
  ) {
    clinicalItems.push({
      id: "facility-justification",
      label: "Facility justification documented",
      description: "Clinical reasons for requested care setting",
      category: "clinical",
      required: true,
    });
    clinicalItems.push({
      id: "patient-acuity",
      label: "Patient acuity level",
      description: "ASA class, comorbidities, monitoring needs",
      category: "clinical",
      required: true,
    });
    clinicalItems.push({
      id: "safety-requirements",
      label: "Safety requirements",
      description: "Equipment, staffing, emergency backup needs",
      category: "clinical",
      required: true,
    });
  }

  if (clinicalItems.length > 0) {
    groups.push({ title: "Clinical Documentation", items: clinicalItems });
  }

  // Prior Treatment Evidence Group
  const treatmentItems: ChecklistItem[] = [];

  if (
    denialReason.includes("step therapy") ||
    denialReason.includes("step-therapy")
  ) {
    treatmentItems.push({
      id: "step-therapy-attempts",
      label: "Step therapy attempts",
      description: "Document each required medication tried",
      category: "documentation",
      required: true,
    });
    treatmentItems.push({
      id: "failure-reasons",
      label: "Failure/intolerance documented",
      description: "Why each prior treatment didn't work",
      category: "documentation",
      required: true,
    });
  }

  if (
    denialReason.includes("not medically necessary") ||
    denialReason.includes("medical necessity")
  ) {
    treatmentItems.push({
      id: "severity-evidence",
      label: "Severity documented",
      description: "Objective measures, scores, or exam findings",
      category: "documentation",
      required: true,
    });
    treatmentItems.push({
      id: "treatment-alternatives",
      label: "Alternatives considered",
      description: "Why other options aren't appropriate",
      category: "documentation",
      required: true,
    });
  }

  if (
    denialReason.includes("experimental") ||
    denialReason.includes("investigational")
  ) {
    treatmentItems.push({
      id: "fda-approval",
      label: "FDA approval status",
      description: "Indication and approval documentation",
      category: "documentation",
      required: true,
    });
    treatmentItems.push({
      id: "literature-support",
      label: "Literature support",
      description: "Peer-reviewed evidence or guidelines",
      category: "documentation",
      required: true,
    });
  }

  // Site of care specific documentation
  if (
    denialReason.includes("site of care") ||
    denialReason.includes("facility")
  ) {
    treatmentItems.push({
      id: "asc-comparison",
      label: "ASC vs Hospital comparison",
      description: "Why ASC/office setting is inappropriate",
      category: "documentation",
      required: true,
    });
    treatmentItems.push({
      id: "prior-outcomes",
      label: "Prior procedure outcomes",
      description: "Complications or issues in lower-acuity settings",
      category: "documentation",
      required: false,
    });
  }

  if (caseData.tried_treatments && caseData.tried_treatments.length > 0) {
    treatmentItems.push({
      id: "prior-treatments",
      label: "Prior treatments ready",
      description: `${caseData.tried_treatments.length} treatments documented`,
      category: "documentation",
      required: true,
    });
  }

  if (treatmentItems.length > 0) {
    groups.push({ title: "Evidence for This Denial", items: treatmentItems });
  }

  // Appeals Process Group
  const appealsItems: ChecklistItem[] = [];

  appealsItems.push({
    id: "denial-letter",
    label: "Denial letter reviewed",
    description: "Know exact criteria cited",
    category: "appeals",
    required: true,
  });

  appealsItems.push({
    id: "guideline-reference",
    label: "Guidelines identified",
    description: "ACR, ACOG, specialty society support",
    category: "appeals",
    required: false,
  });

  appealsItems.push({
    id: "peer-reviewer-info",
    label: "Peer reviewer specialty",
    description: "Request same-specialty review if needed",
    category: "appeals",
    required: false,
  });

  groups.push({ title: "Call Preparation", items: appealsItems });

  return groups;
}

// Insurer-specific tips
function getInsurerTips(
  insurer: string
): { tip: string; priority: "high" | "medium" | "low" }[] {
  const tips: { tip: string; priority: "high" | "medium" | "low" }[] = [];
  const insurerLower = insurer.toLowerCase();

  if (
    insurerLower.includes("united") ||
    insurerLower.includes("uhc") ||
    insurerLower.includes("optum")
  ) {
    tips.push({
      tip: "Lead with service intensity — document nursing needs and monitoring frequency",
      priority: "high",
    });
    tips.push({
      tip: "Document acute changes or worsening clearly",
      priority: "medium",
    });
    tips.push({
      tip: "Highlight acute clinical changes and care escalation needs",
      priority: "low",
    });
  } else if (insurerLower.includes("cigna")) {
    tips.push({
      tip: "Lead with objective clinical findings and functional impact",
      priority: "high",
    });
    tips.push({
      tip: "Quantify functional limitations with scores",
      priority: "medium",
    });
  } else if (insurerLower.includes("aetna")) {
    tips.push({
      tip: "Review their published clinical policy for specific requirements",
      priority: "high",
    });
    tips.push({
      tip: "Reference policy number if applicable",
      priority: "medium",
    });
  } else if (
    insurerLower.includes("anthem") ||
    insurerLower.includes("bcbs") ||
    insurerLower.includes("blue cross") ||
    insurerLower.includes("blue shield")
  ) {
    tips.push({
      tip: "Criteria varies by state — confirm local policy",
      priority: "high",
    });
    tips.push({
      tip: "Focus on clinical severity and service needs",
      priority: "medium",
    });
  } else if (insurerLower.includes("humana")) {
    tips.push({
      tip: "Check Humana Medical Coverage Policy",
      priority: "high",
    });
    tips.push({
      tip: "Medicare Advantage cases follow CMS guidelines",
      priority: "medium",
    });
  } else if (insurerLower.includes("kaiser")) {
    tips.push({
      tip: "Internal review — request clinical rationale",
      priority: "high",
    });
    tips.push({
      tip: "Escalate to medical director if needed",
      priority: "medium",
    });
  } else if (insurerLower.includes("medicaid")) {
    tips.push({ tip: "State-specific criteria apply", priority: "high" });
    tips.push({ tip: "Reference state Medicaid manual", priority: "medium" });
  } else if (insurerLower.includes("medicare")) {
    tips.push({ tip: "Reference LCD/NCD for your region", priority: "high" });
    tips.push({
      tip: "CMS guidelines are publicly available",
      priority: "medium",
    });
  }

  // Generic tips if no specific insurer matched
  if (tips.length === 0) {
    tips.push({
      tip: "Ask what specific clinical criteria they need documented",
      priority: "medium",
    });
    tips.push({
      tip: "Request peer-to-peer with same specialty",
      priority: "low",
    });
  }

  return tips;
}

function DocumentChecklistPanel({
  caseData,
  callMode,
}: {
  caseData: AdaptedCase;
  callMode: boolean;
}) {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);

  const checklistGroups = useMemo(
    () => generateDynamicChecklist(caseData),
    [caseData]
  );
  const insurerTips = useMemo(
    () => getInsurerTips(caseData.insurance_company || ""),
    [caseData.insurance_company]
  );

  const totalItems = checklistGroups.reduce(
    (sum, group) => sum + group.items.length,
    0
  );
  const checkedCount = checkedItems.size;
  const requiredItems = checklistGroups
    .flatMap((g) => g.items)
    .filter((i) => i.required);
  const requiredChecked = requiredItems.filter((i) =>
    checkedItems.has(i.id)
  ).length;
  const allRequiredDone = requiredChecked === requiredItems.length;

  const toggleItem = (id: string) => {
    setCheckedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleNoteExpand = (id: string) => {
    setExpandedNotes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Don't show in call mode
  if (callMode) return null;

  return (
    <>
      {/* Toggle Button - Fixed on right side */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-0 top-1/3 z-40 flex items-center gap-2 px-3 py-3 bg-white border border-r-0 border-slate-200 rounded-l-xl shadow-lg hover:bg-slate-50 transition-all ${
          isOpen ? "translate-x-full opacity-0" : "translate-x-0 opacity-100"
        }`}
        style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
      >
        <IconDocument className="w-4 h-4 text-slate-600 rotate-90" />
        <span className="text-xs font-medium text-slate-700">
          Prep {checkedCount}/{totalItems}
        </span>
        {!allRequiredDone && requiredItems.length > 0 && (
          <span className="w-2 h-2 bg-amber-400 rounded-full" />
        )}
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-96 max-w-[90vw] bg-white border-l border-slate-200 shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center">
              <IconDocument className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Prep Checklist</h3>
              <p className="text-xs text-slate-500">
                {checkedCount}/{totalItems} ready
                {!allRequiredDone && requiredItems.length > 0 && (
                  <span className="ml-2 text-amber-600">
                    • {requiredItems.length - requiredChecked} required
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-lg hover:bg-slate-200 flex items-center justify-center transition-colors"
          >
            <svg
              className="w-5 h-5 text-slate-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-4 py-2 border-b border-slate-100">
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                allRequiredDone ? "bg-teal-500" : "bg-slate-400"
              }`}
              style={{
                width: `${
                  totalItems > 0 ? (checkedCount / totalItems) * 100 : 0
                }%`,
              }}
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto h-[calc(100%-140px)] p-4 space-y-4">
          {/* Insurer Tips - Reviewer Playbook */}
          {insurerTips.length > 0 && (
            <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-xl">
              <h4 className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">
                {caseData.insurance_company} Playbook
              </h4>
              <ul className="space-y-1.5">
                {insurerTips.map((tip, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-xs text-blue-700"
                  >
                    <span
                      className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        tip.priority === "high"
                          ? "bg-blue-500"
                          : tip.priority === "medium"
                          ? "bg-blue-300"
                          : "bg-blue-200"
                      }`}
                    />
                    {tip.tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Checklist Groups */}
          {checklistGroups.map((group, groupIdx) => (
            <div
              key={groupIdx}
              className="bg-white border border-slate-200 rounded-xl overflow-hidden"
            >
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  {group.title}
                </h4>
              </div>
              <div className="divide-y divide-slate-100">
                {group.items.map((item) => {
                  const isChecked = checkedItems.has(item.id);
                  const hasNote = notes[item.id]?.trim();
                  const isNoteExpanded = expandedNotes.has(item.id);

                  return (
                    <div key={item.id} className="p-3">
                      <div className="flex items-start gap-2.5">
                        {/* Checkbox */}
                        <button
                          onClick={() => toggleItem(item.id)}
                          className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                            isChecked
                              ? "bg-teal-500 border-teal-500"
                              : "border-slate-300 hover:border-teal-400"
                          }`}
                        >
                          {isChecked && (
                            <IconCheck className="w-2.5 h-2.5 text-white" />
                          )}
                        </button>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-medium leading-tight ${
                                isChecked ? "text-slate-400" : "text-slate-700"
                              }`}
                            >
                              {item.label}
                            </span>
                            {item.required && !isChecked && (
                              <span className="text-[9px] text-amber-600 bg-amber-50 px-1 py-0.5 rounded">
                                required
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                            {item.description}
                          </p>

                          {/* Note toggle/input */}
                          {isNoteExpanded ? (
                            <div className="mt-2">
                              <textarea
                                value={notes[item.id] || ""}
                                onChange={(e) =>
                                  setNotes((prev) => ({
                                    ...prev,
                                    [item.id]: e.target.value,
                                  }))
                                }
                                placeholder="Add note or paste evidence..."
                                className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none resize-none"
                                rows={2}
                              />
                              <button
                                onClick={() => toggleNoteExpand(item.id)}
                                className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                              >
                                Done
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => toggleNoteExpand(item.id)}
                              className="mt-1 text-xs text-slate-400 hover:text-teal-600 flex items-center gap-1"
                            >
                              <IconPencil className="w-2.5 h-2.5" />
                              {hasNote ? "Edit note" : "Add note"}
                            </button>
                          )}

                          {/* Show note preview when collapsed */}
                          {hasNote && !isNoteExpanded && (
                            <p className="mt-1 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded truncate">
                              {notes[item.id]}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Ready state message */}
          {allRequiredDone && (
            <div className="p-3 bg-teal-50 border border-teal-100 rounded-xl">
              <p className="text-sm text-teal-700 text-center">
                ✓ All required items ready
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================
// KEY TALKING POINTS - CLINICAL ARGUMENTS
// Fast checklist with selectable pills + structured output
// ============================================

// Clinical arguments data model (for DB persistence)
// Dynamic: keyed by whatever category IDs the engine produces
interface ClinicalArgumentsData {
  selected: Record<
    string,
    {
      enabled: boolean;
      tags: string[];
      extras?: Record<string, string>;
      other?: string;
    }
  >;
  classification?: {
    serviceType: string;
    specialtyDomain: string;
  };
  lastUpdatedAt: string;
}

// ============================================
// NOTE SUGGESTION TYPES
// ============================================

type SuggestionStatus = "affirmed" | "negated" | "uncertain";
type ConfidenceLevel = "high" | "medium" | "low";

interface TagSuggestion {
  value: string;
  status: SuggestionStatus;
  evidence: string[];
}

interface FieldSuggestion<T> {
  value: T | null;
  status: SuggestionStatus;
  evidence: string[];
}

interface CategorySuggestion {
  confidence: ConfidenceLevel;
  recommend_apply: boolean;
  tags: TagSuggestion[];
  otherText?: FieldSuggestion<string>;
}

interface NeuroRedFlagsSuggestion extends CategorySuggestion {
  onset?: FieldSuggestion<string>;
}

interface ProgressiveDeteriorationSuggestion extends CategorySuggestion {
  painFrom?: FieldSuggestion<number>;
  painTo?: FieldSuggestion<number>;
  durationWeeks?: FieldSuggestion<number>;
}

interface FailedConservativeTxSuggestion extends CategorySuggestion {
  durationWeeks?: FieldSuggestion<number>;
  ptYesNo?: FieldSuggestion<"yes" | "no">;
}

interface TreatmentPlanningSuggestion extends CategorySuggestion {
  rationale?: FieldSuggestion<string>;
}

interface DifferentialDiagnosisSuggestion extends CategorySuggestion {
  findings?: FieldSuggestion<string>;
}

interface NoteSuggestionsResponse {
  suggestions: {
    neuroRedFlags: NeuroRedFlagsSuggestion;
    progressiveDeterioration: ProgressiveDeteriorationSuggestion;
    failedConservativeTx: FailedConservativeTxSuggestion;
    treatmentPlanning: TreatmentPlanningSuggestion;
    differentialDiagnosis: DifferentialDiagnosisSuggestion;
  };
  meta: {
    note_type_guess: string;
    warnings: string[];
  };
}

// Map from suggestion tag values to pill IDs
const TAG_TO_PILL_ID: Record<string, string> = {
  // Neuro red flags
  Weakness: "weakness",
  Numbness: "numbness",
  "Reflex changes": "reflex",
  "Bowel/bladder dysfunction": "bowel_bladder",
  "Bowel/bladder": "bowel_bladder",
  "Saddle anesthesia": "saddle",
  "Progressive deficit": "progressive",
  "Severe radiculopathy": "radiculopathy",
  // Progressive deterioration
  "Pain worsening": "pain_worsening",
  "Function declining": "function_decline",
  "ADL impact": "adl_impact",
  "Work limited": "work_limited",
  "Sleep disturbed": "sleep_disturbed",
  "Mobility reduced": "mobility_reduced",
  // Failed conservative
  NSAIDs: "nsaids",
  "Physical therapy": "pt",
  "Activity modification": "activity_mod",
  "Oral steroids": "oral_steroids",
  "Steroids (oral)": "oral_steroids",
  "Epidural injections": "epidural",
  "Gabapentin/pregabalin": "gabapentin",
  "Muscle relaxants": "muscle_relaxants",
  "Home exercise": "hep",
  // Treatment planning
  "Surgery consult": "surgery_consult",
  Injection: "injection",
  "Targeted therapy": "targeted_therapy",
  "Specialist referral": "specialist",
  "Rule out structural cause": "rule_out",
  // Differential diagnosis
  "Disc herniation": "herniation",
  "Spinal stenosis": "stenosis",
  "Nerve compression": "nerve_compression",
  Fracture: "fracture",
  Infection: "infection",
  "Tumor/malignancy": "tumor",
  "Cauda equina": "cauda_equina",
  // Generic
  Other: "other",
};

// Selectable pill component with aria-pressed
function SelectablePill({
  label,
  selected,
  onToggle,
  isOther,
  otherValue,
  onOtherChange,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
  isOther?: boolean;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        role="checkbox"
        aria-pressed={selected}
        aria-checked={selected}
        onClick={onToggle}
        className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all duration-75 ${
          selected
            ? "bg-slate-700 text-white border-slate-700"
            : "bg-white text-slate-600 border-slate-300 hover:border-slate-400 hover:bg-slate-50"
        }`}
      >
        {label}
      </button>
      {isOther && selected && onOtherChange && (
        <input
          type="text"
          aria-label={`Specify ${label}`}
          value={otherValue || ""}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder="Specify..."
          className="h-6 w-24 text-xs border border-slate-300 rounded px-2 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:border-slate-500"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}

// Compact extra field component
function ExtraField({
  field,
  value,
  onChange,
}: {
  field: TalkingPointExtra;
  value: string;
  onChange: (value: string) => void;
}) {
  const widthClass = {
    xs: "w-12",
    sm: "w-20",
    md: "w-32",
  }[field.width];

  const inputClass = `h-6 text-xs border border-slate-300 rounded px-2 bg-white text-slate-700 placeholder-slate-400 
    focus:outline-none focus:border-slate-500 transition-colors`;

  return (
    <div className="flex items-center gap-1">
      {field.label !== "→" ? (
        <label className="text-[11px] text-slate-500 whitespace-nowrap">
          {field.label}
        </label>
      ) : (
        <span className="text-[11px] text-slate-400">→</span>
      )}
      <input
        type={field.type === "number" ? "number" : "text"}
        aria-label={field.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={`${inputClass} ${widthClass} ${
          field.type === "number" ? "text-center" : ""
        }`}
      />
      {field.suffix && (
        <span className="text-[11px] text-slate-500">{field.suffix}</span>
      )}
    </div>
  );
}

// Expandable clinical argument row
function ClinicalArgumentRow({
  config,
  selectedPills,
  onTogglePill,
  otherValues,
  onOtherChange,
  extraValues,
  onExtraChange,
  isExpanded,
  onToggleExpand,
  isLast,
}: {
  config: TalkingPointCategory;
  selectedPills: Set<string>;
  onTogglePill: (pillId: string) => void;
  otherValues: Record<string, string>;
  onOtherChange: (pillId: string, value: string) => void;
  extraValues: Record<string, string>;
  onExtraChange: (fieldId: string, value: string) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isLast: boolean;
}) {
  const hasSelections = selectedPills.size > 0;
  const selectionCount = selectedPills.size;

  return (
    <div
      className={`${!isLast ? "border-b border-slate-200" : ""} ${
        hasSelections ? "bg-slate-50/50" : "bg-white"
      }`}
    >
      {/* Row header - clickable to expand */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-slate-50/80 transition-colors"
        aria-expanded={isExpanded}
      >
        {/* Completion indicator */}
        <div
          className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            hasSelections ? "border-slate-600 bg-slate-600" : "border-slate-300"
          }`}
        >
          {hasSelections && (
            <svg
              className="w-2.5 h-2.5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>

        {/* Label + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-semibold ${
                hasSelections ? "text-slate-700" : "text-slate-600"
              }`}
            >
              {config.label}
            </span>
            {selectionCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">
                {selectionCount}
              </span>
            )}
          </div>
          {!isExpanded && (
            <p className="text-[11px] text-slate-400 truncate">
              {config.description}
            </p>
          )}
        </div>

        {/* Expand chevron */}
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-150 ${
            isExpanded ? "rotate-90" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-2.5 ml-6">
          <p className="text-[11px] text-slate-500 mb-2">
            {config.description}
          </p>

          {/* Pills */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {config.pills.map((pill) => (
              <SelectablePill
                key={pill.id}
                label={pill.label}
                selected={selectedPills.has(pill.id)}
                onToggle={() => onTogglePill(pill.id)}
                isOther={pill.isOther}
                otherValue={otherValues[pill.id]}
                onOtherChange={
                  pill.isOther
                    ? (value) => onOtherChange(pill.id, value)
                    : undefined
                }
              />
            ))}
          </div>

          {/* Extra fields - only show when relevant */}
          {config.extras && hasSelections && (
            <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-slate-100">
              {config.extras.map((field) => (
                <ExtraField
                  key={field.id}
                  field={field}
                  value={extraValues[field.id] || ""}
                  onChange={(value) => onExtraChange(field.id, value)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Structured talking points display
function StructuredTalkingPoints({
  bullets,
  callMode,
  showEmptyState = false,
}: {
  bullets: string[];
  callMode: boolean;
  showEmptyState?: boolean;
}) {
  const text = bullets.join("\n");
  const isEmpty = bullets.length === 0;

  // In call mode, don't show empty state - just hide
  if (isEmpty && callMode) return null;

  // In normal mode with showEmptyState, show the empty state
  if (isEmpty && !showEmptyState) return null;

  return (
    <div
      className={`rounded border ${
        callMode
          ? "bg-slate-800/30 border-slate-700"
          : "bg-teal-50/50 border-teal-200"
      }`}
    >
      <div
        className={`px-3 py-2 border-b flex items-center justify-between ${
          callMode ? "border-slate-700" : "border-teal-200"
        }`}
      >
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${
            callMode ? "text-slate-400" : "text-teal-700"
          }`}
        >
          Structured Talking Points
        </span>
        {!isEmpty && <CopyButton text={text} callMode={callMode} />}
      </div>
      {isEmpty ? (
        <div className="px-3 py-4 text-center">
          <p className="text-xs text-slate-500">
            Select clinical arguments below or use "Suggest from note" to
            generate concise talking points.
          </p>
        </div>
      ) : (
        <ul className="px-3 py-2 space-y-1">
          {bullets.map((bullet, i) => (
            <li
              key={i}
              className={`text-xs leading-relaxed flex items-start gap-2 ${
                callMode ? "text-slate-300" : "text-slate-700"
              }`}
            >
              <span className={callMode ? "text-teal-400" : "text-teal-600"}>
                •
              </span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface CriteriaMappingProps {
  categories: TalkingPointCategory[];
  selectedPills: Record<string, Set<string>>;
  classification: CaseClassification | null;
  callMode?: boolean;
}

/**
 * Generic criteria mapping — derives evidence from whatever
 * dynamic categories have active selections. No hardcoded maps.
 */
function CriteriaMapping({
  categories,
  selectedPills,
  classification,
  callMode = false,
}: CriteriaMappingProps) {
  const severityEvidence: string[] = [];
  const intensityEvidence: string[] = [];

  for (const cat of categories) {
    const pills = selectedPills[cat.id];
    if (!pills || pills.size === 0) continue;

    const selectedLabels = cat.pills
      .filter((p) => pills.has(p.id) && !p.isOther)
      .map((p) => p.label.toLowerCase());

    const catId = cat.id.toLowerCase();

    // Severity of illness indicators
    if (
      catId.includes("neuro") ||
      catId.includes("severity") ||
      catId.includes("diagnostic") ||
      catId.includes("obgyn") ||
      catId.includes("cardio") ||
      catId.includes("msk") ||
      catId.includes("red") ||
      catId.includes("objective")
    ) {
      if (selectedLabels.length > 0) {
        severityEvidence.push(
          `${cat.label}: ${selectedLabels.slice(0, 3).join(", ")} documented`
        );
      }
    }

    if (catId.includes("risk") || catId.includes("delay")) {
      severityEvidence.push("Clinical urgency — risk of harm if delayed");
    }

    // Intensity of service indicators
    if (
      catId.includes("failed") ||
      catId.includes("conservative") ||
      catId.includes("med_failure") ||
      catId.includes("prior_med")
    ) {
      intensityEvidence.push(
        `Failed adequate trial of ${selectedLabels.slice(0, 3).join(", ")}`
      );
    }

    if (catId.includes("duration") || catId.includes("persistence")) {
      intensityEvidence.push(
        "Persistent symptoms despite conservative management"
      );
    }

    if (catId.includes("guideline") || catId.includes("criteria")) {
      intensityEvidence.push(
        "Published guideline criteria met for this request"
      );
    }

    if (catId.includes("diagnostic") || catId.includes("uncertainty")) {
      intensityEvidence.push(
        "Imaging/evaluation required to exclude serious pathology"
      );
    }

    if (catId.includes("functional")) {
      intensityEvidence.push("Significant functional limitation documented");
    }

    if (catId.includes("imaging") && catId.includes("finding")) {
      intensityEvidence.push(
        "Imaging confirms pathology correlating with symptoms"
      );
    }

    if (catId.includes("contraindication") || catId.includes("alternative")) {
      intensityEvidence.push(
        "Contraindications to standard alternatives documented"
      );
    }
  }

  // Deduplicate
  const uniqueSeverity = [...new Set(severityEvidence)];
  const uniqueIntensity = [...new Set(intensityEvidence)];
  const hasAnyCriteria =
    uniqueSeverity.length > 0 || uniqueIntensity.length > 0;

  // ── Call mode: compact dark ──
  if (callMode) {
    if (!hasAnyCriteria) return null;

    return (
      <div className="mt-2 rounded border border-slate-700 bg-slate-800/30 overflow-hidden">
        <div className="px-3 py-1.5 border-b border-slate-700">
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
            Criteria Met
          </span>
        </div>
        <div className="px-3 py-2 space-y-2">
          {uniqueSeverity.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-500 mb-1">
                Severity of Illness
              </p>
              {uniqueSeverity.map((e, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1.5 text-[11px] text-slate-400"
                >
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>{e}</span>
                </div>
              ))}
            </div>
          )}
          {uniqueIntensity.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-500 mb-1">
                Intensity of Service
              </p>
              {uniqueIntensity.map((e, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1.5 text-[11px] text-slate-400"
                >
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>{e}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Normal mode ──
  return (
    <div className="rounded border border-slate-200 bg-gradient-to-b from-slate-50/80 to-white overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Criteria Mapping
          </span>
          {hasAnyCriteria && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
              Auto-generated
            </span>
          )}
        </div>
      </div>

      {!hasAnyCriteria ? (
        <div className="px-3 py-4 text-center">
          <p className="text-xs text-slate-400 italic">
            Select clinical arguments below to auto-generate criteria
            justification.
          </p>
        </div>
      ) : (
        <div className="p-3 space-y-3">
          {uniqueSeverity.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">
                Severity of Illness — Evidence
              </p>
              <div className="space-y-1">
                {uniqueSeverity.map((evidence, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 text-xs text-slate-600"
                  >
                    <span className="text-emerald-500 mt-0.5 flex-shrink-0">
                      ✓
                    </span>
                    <span>{evidence}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {uniqueIntensity.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">
                Intensity of Service — Justification
              </p>
              <div className="space-y-1">
                {uniqueIntensity.map((evidence, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 text-xs text-slate-600"
                  >
                    <span className="text-emerald-500 mt-0.5 flex-shrink-0">
                      ✓
                    </span>
                    <span>{evidence}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// EVIDENCE & GUIDELINE SUPPORT - DOCUMENT-LEVEL CITATIONS
// Uses guidelineResolver to map LLM output to verified, document-level sources.
// Flow: LLM output → parse → resolve → validate → render
// ============================================

interface GuidelineSupportProps {
  content: string;
  callMode?: boolean;
}

// Inline SVG: external-link icon (Lucide-style, 16px)
function ExternalLinkIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// Verified shield badge (inline SVG)
function VerifiedBadge({
  dark = false,
  size = "sm",
}: {
  dark?: boolean;
  size?: "sm" | "xs";
}) {
  const sizeClass = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";
  const colorClass = dark ? "text-emerald-500" : "text-emerald-600";
  return (
    <span
      className={`inline-flex items-center ml-1.5 ${colorClass}`}
      title="Verified document-level source"
    >
      <svg
        className={sizeClass}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11.5 14.5 15.5 10" />
      </svg>
    </span>
  );
}

// "View Source" button for verified evidence
function ViewSourceButton({
  url,
  label,
  dark = false,
}: {
  url: string;
  label: string;
  dark?: boolean;
}) {
  const colorClass = dark
    ? "text-slate-500 hover:text-slate-300"
    : "text-slate-400 hover:text-slate-600";
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`p-1 rounded transition-colors flex-shrink-0 ${colorClass}`}
      title="View source document"
      aria-label={`View source for ${label}`}
    >
      <ExternalLinkIcon className="w-4 h-4" />
    </a>
  );
}

function GuidelineSupport({
  content,
  callMode = false,
}: GuidelineSupportProps) {
  const items = useMemo(() => parseAndResolveEvidence(content), [content]);

  if (items.length === 0) {
    return (
      <p
        className={`text-sm ${callMode ? "text-slate-500" : "text-slate-400"}`}
      >
        No guideline references available.
      </p>
    );
  }

  // Call mode: compact dark theme
  if (callMode) {
    return (
      <div className="space-y-2.5">
        {items.map((item, idx) => {
          const linkUrl = item.verified
            ? item.canonical_url
            : item.url_quality === "DOCUMENT_SPECIFIC"
            ? item.url
            : null;
          return (
            <div
              key={`evidence-${idx}`}
              className="flex items-start gap-2.5 text-[12px] leading-relaxed"
            >
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-slate-300">
                  {item.display_label}
                </span>
                {item.verified && <VerifiedBadge dark size="xs" />}
                <span className="text-slate-600 mx-1.5">—</span>
                <span className="text-slate-400">
                  {item.recommendation_text}
                </span>
              </div>
              {linkUrl && (
                <ViewSourceButton
                  url={linkUrl}
                  label={item.display_label}
                  dark
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Normal mode: document-level citations
  return (
    <div>
      <div className="space-y-2.5">
        {items.map((item, idx) => {
          const linkUrl = item.verified
            ? item.canonical_url
            : item.url_quality === "DOCUMENT_SPECIFIC"
            ? item.url
            : null;
          return (
            <div key={`evidence-${idx}`} className="flex items-start gap-2.5">
              {/* Content */}
              <div className="flex-1 min-w-0 text-[13px] leading-relaxed">
                <span className="font-semibold text-slate-800">
                  {item.display_label}
                </span>
                {item.verified && <VerifiedBadge />}
                {!item.verified && !linkUrl && (
                  <span
                    className="inline-flex items-center ml-1.5 text-[10px] text-slate-400"
                    title="Source not verified"
                  >
                    <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-400">
                      unverified
                    </span>
                  </span>
                )}
                <span className="text-slate-400 mx-1.5">—</span>
                <span className="text-slate-600">
                  {item.recommendation_text}
                </span>
              </div>

              {/* View Source button — only for document-specific URLs */}
              {linkUrl && (
                <ViewSourceButton url={linkUrl} label={item.display_label} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// RISK IF DENIED — CAUSALITY-BASED CLINICAL HARMS
// Denial/delay → concrete patient harm.
// Two tiers: immediate (hours–days) vs delayed/irreversible (weeks–months).
// Labels: clinician-grade, defensible, 3–7 words.
// Rationale: optional 1-sentence hover detail (≤22 words).
// ============================================

interface HarmChip {
  id: string;
  label: string;
  rationale?: string;
  tier: "immediate" | "delayed";
}

interface RiskIfDeniedProps {
  content: string;
  caseId: string;
  callMode?: boolean;
  caseData?: Case | null;
}

// Rationale tooltip — small "i" icon with popover
function RationaleTooltip({
  text,
  dark = false,
}: {
  text: string;
  dark?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        onClick={() => setOpen(!open)}
        className={`ml-1 w-3.5 h-3.5 rounded-full inline-flex items-center justify-center text-[9px] font-bold transition-colors ${
          dark
            ? "bg-white/10 text-white/50 hover:bg-white/20 hover:text-white/80"
            : "bg-slate-200 text-slate-500 hover:bg-slate-300 hover:text-slate-700"
        }`}
        aria-label="More detail about this risk"
      >
        i
      </button>
      {open && (
        <div
          className={`absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 px-2.5 py-2 rounded-lg shadow-lg text-xs leading-relaxed ${
            dark
              ? "bg-slate-700 text-slate-200 border border-slate-600"
              : "bg-white text-slate-700 border border-slate-200"
          }`}
          role="tooltip"
        >
          {text}
        </div>
      )}
    </div>
  );
}

// ── Specialty-keyed harm vocabularies ───────────────────
// tier: "immediate" = hours–days | "delayed" = weeks–months/irreversible
// Labels use causality language; rationale adds clinical context.

const HARM_CHIPS: Record<string, HarmChip[]> = {
  obgyn: [
    // immediate: life-threat → transfusion → ED
    {
      id: "hemorrhage",
      label: "Acute hemorrhage",
      tier: "immediate",
      rationale:
        "Delay risks hemodynamic instability from uncontrolled uterine bleeding.",
    },
    {
      id: "anemia_transfusion",
      label: "Transfusion-requiring anemia",
      tier: "immediate",
      rationale:
        "Ongoing blood loss without evaluation increases transfusion likelihood.",
    },
    {
      id: "emergency_ed",
      label: "Emergency department visit",
      tier: "immediate",
      rationale:
        "Denial increases risk of avoidable ED presentation for acute bleeding.",
    },
    // delayed: malignancy → irreversible → progression
    {
      id: "delayed_malignancy",
      label: "Delayed diagnosis of malignancy",
      tier: "delayed",
      rationale:
        "Persistent abnormal bleeding warrants timely tissue evaluation to exclude cancer.",
    },
    {
      id: "fertility",
      label: "Irreversible fertility loss",
      tier: "delayed",
      rationale: "Delay narrows fertility-preserving treatment options.",
    },
    {
      id: "disease_progression",
      label: "Disease progression",
      tier: "delayed",
      rationale:
        "Untreated underlying pathology risks worsening and need for invasive intervention.",
    },
  ],
  neuro: [
    // immediate: life-threat → rapid deficit → ED
    {
      id: "cauda_equina",
      label: "Cauda equina syndrome",
      tier: "immediate",
      rationale:
        "Delay risks permanent bowel/bladder dysfunction and motor loss.",
    },
    {
      id: "herniation",
      label: "Uncal herniation",
      tier: "immediate",
      rationale:
        "Undetected mass lesion risks fatal herniation without timely imaging.",
    },
    {
      id: "acute_deficit",
      label: "Rapid neurologic deterioration",
      tier: "immediate",
      rationale:
        "Progressive weakness may become irreversible within hours of onset.",
    },
    // delayed: permanent deficit → ED
    {
      id: "permanent_deficit",
      label: "Permanent neurologic deficit",
      tier: "delayed",
      rationale: "Delay in diagnosis risks irreversible motor or sensory loss.",
    },
    {
      id: "emergency_ed",
      label: "Avoidable ED visit",
      tier: "delayed",
      rationale:
        "Denial increases likelihood of emergent presentation for worsening symptoms.",
    },
  ],
  msk: [
    // immediate: emergent surgery → instability → ED
    {
      id: "emergency_surgery",
      label: "Emergent surgical intervention",
      tier: "immediate",
      rationale:
        "Delay risks structural failure requiring urgent operative repair.",
    },
    {
      id: "acute_instability",
      label: "Acute joint instability",
      tier: "immediate",
      rationale:
        "Undiagnosed structural damage risks acute mechanical failure and fall.",
    },
    // delayed: joint destruction → chronic pain → disability
    {
      id: "joint_destruction",
      label: "Irreversible joint destruction",
      tier: "delayed",
      rationale:
        "Ongoing mechanical damage without diagnosis risks permanent joint compromise.",
    },
    {
      id: "chronic_pain",
      label: "Chronic pain syndrome",
      tier: "delayed",
      rationale:
        "Untreated injury risks central sensitization and refractory pain.",
    },
    {
      id: "disability",
      label: "Permanent functional disability",
      tier: "delayed",
      rationale:
        "Delay in treatment risks loss of function that cannot be recovered.",
    },
  ],
  pain: [
    // immediate: pain crisis → ED
    {
      id: "acute_crisis",
      label: "Uncontrolled pain crisis",
      tier: "immediate",
      rationale:
        "Untreated severe pain drives ED utilization and hemodynamic stress.",
    },
    {
      id: "emergency_ed",
      label: "Emergency department visit",
      tier: "immediate",
      rationale:
        "Denial of outpatient treatment increases avoidable acute care visits.",
    },
    // delayed: sensitization → opioid → disability
    {
      id: "central_sensitization",
      label: "Central sensitization",
      tier: "delayed",
      rationale:
        "Prolonged untreated pain risks permanent pain pathway remodeling.",
    },
    {
      id: "opioid_dependence",
      label: "Escalating opioid dependence",
      tier: "delayed",
      rationale:
        "Failure to treat the underlying cause drives opioid dose escalation.",
    },
    {
      id: "disability",
      label: "Permanent functional disability",
      tier: "delayed",
      rationale:
        "Delay risks progressive deconditioning and irreversible functional loss.",
    },
  ],
  cardio: [
    // immediate: MI → death → heart failure
    {
      id: "mi",
      label: "Myocardial infarction",
      tier: "immediate",
      rationale:
        "Delay risks undetected ischemia progressing to acute infarction.",
    },
    {
      id: "sudden_death",
      label: "Sudden cardiac death",
      tier: "immediate",
      rationale:
        "Undiagnosed structural or electrical abnormality may cause fatal arrhythmia.",
    },
    {
      id: "heart_failure",
      label: "Acute decompensated heart failure",
      tier: "immediate",
      rationale:
        "Delayed evaluation risks progression to acute volume overload and hospitalization.",
    },
    // delayed: stroke
    {
      id: "stroke",
      label: "Cardioembolic stroke",
      tier: "delayed",
      rationale:
        "Untreated embolic source risks stroke with permanent neurologic sequelae.",
    },
  ],
  onc: [
    // immediate: progression → oncologic emergency
    {
      id: "progression",
      label: "Uncontrolled tumor progression",
      tier: "immediate",
      rationale:
        "Treatment delay allows continued growth and potential metastatic spread.",
    },
    {
      id: "emergency_comp",
      label: "Oncologic emergency",
      tier: "immediate",
      rationale:
        "Delay risks cord compression, tumor lysis, or acute hemorrhage.",
    },
    // delayed: stage migration → survival
    {
      id: "stage_migration",
      label: "Disease upstaging",
      tier: "delayed",
      rationale:
        "Delayed diagnosis risks higher stage at detection with fewer treatment options.",
    },
    {
      id: "survival",
      label: "Reduced overall survival",
      tier: "delayed",
      rationale:
        "Each week of treatment delay may worsen long-term survival outcomes.",
    },
  ],
  pulm: [
    // immediate: respiratory failure → intubation
    {
      id: "respiratory_failure",
      label: "Acute respiratory failure",
      tier: "immediate",
      rationale:
        "Undiagnosed pulmonary pathology risks sudden hypoxemic decompensation.",
    },
    {
      id: "emergency_intubation",
      label: "Emergency intubation",
      tier: "immediate",
      rationale:
        "Delay risks airway compromise requiring emergent mechanical ventilation.",
    },
    // delayed: malignancy → organ damage
    {
      id: "delayed_malignancy",
      label: "Delayed diagnosis of lung malignancy",
      tier: "delayed",
      rationale:
        "Delay in tissue evaluation risks upstaging of potentially curable disease.",
    },
    {
      id: "hypoxia",
      label: "Hypoxemic end-organ damage",
      tier: "delayed",
      rationale:
        "Chronic untreated hypoxia risks irreversible cardiac and neurologic injury.",
    },
  ],
  gi: [
    // immediate: perforation → hemorrhage → obstruction
    {
      id: "perforation",
      label: "Bowel perforation",
      tier: "immediate",
      rationale:
        "Delay risks undetected perforation progressing to peritonitis and sepsis.",
    },
    {
      id: "hemorrhage",
      label: "GI hemorrhage",
      tier: "immediate",
      rationale:
        "Active GI bleeding may require emergent endoscopic or surgical intervention.",
    },
    {
      id: "obstruction",
      label: "Complete bowel obstruction",
      tier: "immediate",
      rationale:
        "Delay risks progression to complete obstruction requiring emergency surgery.",
    },
    // delayed: malignancy
    {
      id: "delayed_malignancy",
      label: "Delayed diagnosis of GI malignancy",
      tier: "delayed",
      rationale:
        "Persistent symptoms without evaluation risk missing treatable malignancy.",
    },
  ],
  uro: [
    // immediate: sepsis → obstruction
    {
      id: "sepsis",
      label: "Urosepsis",
      tier: "immediate",
      rationale:
        "Obstructed infected urinary system risks rapid progression to septic shock.",
    },
    {
      id: "obstruction",
      label: "Complete urinary obstruction",
      tier: "immediate",
      rationale:
        "Delay risks acute retention and hydronephrosis with renal compromise.",
    },
    // delayed: renal damage → malignancy
    {
      id: "renal_damage",
      label: "Irreversible renal damage",
      tier: "delayed",
      rationale:
        "Prolonged obstruction risks permanent nephron loss and renal insufficiency.",
    },
    {
      id: "delayed_malignancy",
      label: "Delayed diagnosis of urologic malignancy",
      tier: "delayed",
      rationale:
        "Hematuria or mass without timely workup risks missing curable cancer.",
    },
  ],
  endo: [
    // immediate: DKA → hypoglycemia
    {
      id: "dka",
      label: "Diabetic ketoacidosis",
      tier: "immediate",
      rationale:
        "Inadequate glycemic control risks life-threatening metabolic decompensation.",
    },
    {
      id: "hypoglycemia",
      label: "Severe hypoglycemia",
      tier: "immediate",
      rationale:
        "Denial of appropriate regimen risks recurrent dangerous hypoglycemic episodes.",
    },
    // delayed: organ damage → hospitalization
    {
      id: "organ_damage",
      label: "End-organ damage progression",
      tier: "delayed",
      rationale:
        "Ongoing hyperglycemia accelerates nephropathy, neuropathy, and retinopathy.",
    },
    {
      id: "hospitalization",
      label: "Avoidable hospitalization",
      tier: "delayed",
      rationale:
        "Denial of outpatient management increases risk of inpatient admission.",
    },
  ],
  psych: [
    // immediate: crisis → hospitalization
    {
      id: "crisis",
      label: "Acute psychiatric crisis",
      tier: "immediate",
      rationale:
        "Treatment disruption risks rapid decompensation and safety concerns.",
    },
    {
      id: "hospitalization",
      label: "Psychiatric hospitalization",
      tier: "immediate",
      rationale:
        "Denial of outpatient treatment increases inpatient admission likelihood.",
    },
    // delayed: functional decline
    {
      id: "functional_decline",
      label: "Severe functional decline",
      tier: "delayed",
      rationale:
        "Untreated illness risks progressive occupational and social disability.",
    },
  ],
  general: [
    // immediate: ED → hospitalization
    {
      id: "emergency_ed",
      label: "Emergency department visit",
      tier: "immediate",
      rationale:
        "Denial of timely evaluation increases avoidable ED utilization.",
    },
    {
      id: "hospitalization",
      label: "Avoidable hospitalization",
      tier: "immediate",
      rationale:
        "Delay risks clinical deterioration requiring inpatient admission.",
    },
    // delayed: diagnosis → organ damage
    {
      id: "delayed_diagnosis",
      label: "Delayed serious diagnosis",
      tier: "delayed",
      rationale:
        "Further delay risks progression of undiagnosed serious pathology.",
    },
    {
      id: "organ_damage",
      label: "Irreversible organ damage",
      tier: "delayed",
      rationale:
        "Prolonged untreated disease risks permanent end-organ injury.",
    },
  ],
};

// Derive harms from specialty domain — immediate (hours–days) vs delayed (weeks–months)
function deriveHarmChips(
  specialtyDomain: string,
  clinicalArgs: StoredClinicalArgs | null
): {
  immediate: HarmChip[];
  delayed: HarmChip[];
} {
  const domain = specialtyDomain || "general";
  const chips = HARM_CHIPS[domain] || HARM_CHIPS.general;

  const immediate = chips.filter((c) => c.tier === "immediate");
  const delayed = chips.filter((c) => c.tier === "delayed");

  // If clinical args show specific risk signals, prioritize matching chips
  if (clinicalArgs?.pills) {
    const allPills = Object.values(clinicalArgs.pills).flat();

    // Neuro-specific: cauda equina constellation → ensure CES is first
    if (domain === "neuro") {
      const hasCES = allPills.some((p) =>
        ["saddle", "bowel_bladder", "cauda_equina"].includes(p)
      );
      if (hasCES) {
        const cesIdx = immediate.findIndex((c) => c.id === "cauda_equina");
        if (cesIdx > 0) {
          const [ces] = immediate.splice(cesIdx, 1);
          immediate.unshift(ces);
        }
      }
    }
  }

  // Cap: max 3 per tier (spec says 2–3 strongest)
  return {
    immediate: immediate.slice(0, 3),
    delayed: delayed.slice(0, 3),
  };
}

// Parse harms from AI-generated content as supplemental source
function parseHarmsFromContent(
  content: string,
  specialtyDomain: string
): {
  immediate: HarmChip[];
  delayed: HarmChip[];
} {
  const domain = specialtyDomain || "general";
  const chips = HARM_CHIPS[domain] || HARM_CHIPS.general;
  const immediate: HarmChip[] = [];
  const delayed: HarmChip[] = [];
  const addedIds = new Set<string>();

  const contentLower = content.toLowerCase();

  for (const chip of chips) {
    if (addedIds.has(chip.id)) continue;
    const words = chip.label.toLowerCase().split(/\s+/);
    const keyTerms = words.filter((w) => w.length > 4);
    const matched = keyTerms.some((term) => contentLower.includes(term));
    if (matched) {
      if (chip.tier === "immediate") immediate.push(chip);
      else delayed.push(chip);
      addedIds.add(chip.id);
    }
  }

  return { immediate: immediate.slice(0, 3), delayed: delayed.slice(0, 3) };
}

// Generate bottom line: 1–2 sentences, ≤35 words, must include "medically necessary",
// names top immediate + delayed harm, no passive voice or hedging.
function generateBottomLine(
  immediate: HarmChip[],
  delayed: HarmChip[]
): string {
  const topImmediate = immediate[0];
  const topDelayed = delayed[0];

  if (!topImmediate && !topDelayed) {
    return "This evaluation is medically necessary to prevent avoidable serious harm.";
  }

  // Collect the two strongest harms (one from each tier when possible)
  const harms: string[] = [];
  if (topImmediate) harms.push(topImmediate.label.toLowerCase());
  if (topDelayed && topDelayed.id !== topImmediate?.id) {
    harms.push(topDelayed.label.toLowerCase());
  }

  const harmStr = harms.join(" and ");
  return `This evaluation is medically necessary to prevent avoidable serious harm. Delay increases risk of ${harmStr}.`;
}

// Copy button for bottom line
function BottomLineCopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-1 rounded hover:bg-slate-200/50 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <IconCheck className="w-3.5 h-3.5 text-green-600" />
      ) : (
        <IconCopy className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
      )}
    </button>
  );
}

function RiskIfDeniedSection({
  content,
  caseId,
  callMode = false,
  caseData = null,
}: RiskIfDeniedProps) {
  // Load clinical arguments from localStorage
  const [clinicalArgs, setClinicalArgs] = useState<StoredClinicalArgs | null>(
    null
  );

  useEffect(() => {
    if (caseId) {
      const storageKey = `peerprep.clinicalargs.v4:${caseId}`;
      try {
        const pillsRaw = localStorage.getItem(`${storageKey}:pills`);
        const extrasRaw = localStorage.getItem(`${storageKey}:extras`);

        if (pillsRaw || extrasRaw) {
          setClinicalArgs({
            pills: pillsRaw ? JSON.parse(pillsRaw) : {},
            extras: extrasRaw ? JSON.parse(extrasRaw) : {},
            other: {},
          });
        }
      } catch {
        // ignore errors
      }
    }
  }, [caseId]);

  // Listen for clinical args updates
  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.caseId === caseId) {
        const storageKey = `peerprep.clinicalargs.v4:${caseId}`;
        try {
          const pillsRaw = localStorage.getItem(`${storageKey}:pills`);
          const extrasRaw = localStorage.getItem(`${storageKey}:extras`);
          setClinicalArgs({
            pills: pillsRaw ? JSON.parse(pillsRaw) : {},
            extras: extrasRaw ? JSON.parse(extrasRaw) : {},
            other: {},
          });
        } catch {
          // ignore
        }
      }
    };

    window.addEventListener("clinicalArgsUpdated", handleUpdate);
    return () =>
      window.removeEventListener("clinicalArgsUpdated", handleUpdate);
  }, [caseId]);

  // Classify specialty from caseData
  const specialtyDomain = useMemo(() => {
    if (!caseData) return "general";
    return classifyCase(caseData).specialtyDomain;
  }, [caseData]);

  // Derive harms: immediate (hours–days) vs delayed/irreversible (weeks–months)
  const { immediate, delayed } = useMemo(() => {
    const derived = deriveHarmChips(specialtyDomain, clinicalArgs);

    // If minimal results, supplement from AI-generated content
    if (derived.immediate.length === 0 && derived.delayed.length <= 1) {
      const parsed = parseHarmsFromContent(content, specialtyDomain);
      const existingIds = new Set(
        [...derived.immediate, ...derived.delayed].map((c) => c.id)
      );

      for (const chip of parsed.immediate) {
        if (!existingIds.has(chip.id) && derived.immediate.length < 3) {
          derived.immediate.push(chip);
          existingIds.add(chip.id);
        }
      }
      for (const chip of parsed.delayed) {
        if (!existingIds.has(chip.id) && derived.delayed.length < 3) {
          derived.delayed.push(chip);
          existingIds.add(chip.id);
        }
      }
    }

    return derived;
  }, [clinicalArgs, content, specialtyDomain]);

  const bottomLine = useMemo(
    () => generateBottomLine(immediate, delayed),
    [immediate, delayed]
  );

  // Call mode: compact dark theme with immediate / delayed harm groups
  if (callMode) {
    return (
      <div className="space-y-4">
        {/* Immediate harms (hours–days) */}
        {immediate.length > 0 && (
          <div>
            <p
              className="text-[10px] uppercase tracking-wider text-red-400 font-medium mb-2"
              role="heading"
              aria-level={4}
            >
              Immediate Harm
            </p>
            <div
              className="flex flex-wrap gap-2"
              role="list"
              aria-label="Immediate clinical harms"
            >
              {immediate.map((chip) => (
                <span
                  key={chip.id}
                  role="listitem"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-900/40 text-red-200 border border-red-700/50"
                >
                  <svg
                    className="w-3.5 h-3.5 text-red-400 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  {chip.label}
                  {chip.rationale && (
                    <RationaleTooltip text={chip.rationale} dark />
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Delayed / irreversible harms (weeks–months) */}
        {delayed.length > 0 && (
          <div>
            <p
              className="text-[10px] uppercase tracking-wider text-amber-400 font-medium mb-2"
              role="heading"
              aria-level={4}
            >
              Delayed / Irreversible Harm
            </p>
            <div
              className="flex flex-wrap gap-2"
              role="list"
              aria-label="Delayed or irreversible clinical harms"
            >
              {delayed.map((chip) => (
                <span
                  key={chip.id}
                  role="listitem"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-900/30 text-amber-200 border border-amber-700/50"
                >
                  <svg
                    className="w-3.5 h-3.5 text-amber-400 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {chip.label}
                  {chip.rationale && (
                    <RationaleTooltip text={chip.rationale} dark />
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bottom line */}
        <div className="pt-3 border-t border-slate-700">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
            Bottom Line
          </p>
          <p className="text-sm text-amber-100 font-medium leading-relaxed">
            {bottomLine}
          </p>
        </div>
      </div>
    );
  }

  // Normal mode: clean grouped chip display with rationale tooltips
  return (
    <div className="space-y-5">
      {/* Immediate harms (hours–days) */}
      {immediate.length > 0 && (
        <div>
          <p
            className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-2"
            role="heading"
            aria-level={4}
          >
            Immediate Harm
          </p>
          <div
            className="flex flex-wrap gap-2"
            role="list"
            aria-label="Immediate clinical harms"
          >
            {immediate.map((chip) => (
              <span
                key={chip.id}
                role="listitem"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium bg-red-50 text-red-800 border border-red-200"
              >
                <svg
                  className="w-3.5 h-3.5 text-red-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                {chip.label}
                {chip.rationale && <RationaleTooltip text={chip.rationale} />}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Delayed / irreversible harms (weeks–months) */}
      {delayed.length > 0 && (
        <div>
          <p
            className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-2"
            role="heading"
            aria-level={4}
          >
            Delayed / Irreversible Harm
          </p>
          <div
            className="flex flex-wrap gap-2"
            role="list"
            aria-label="Delayed or irreversible clinical harms"
          >
            {delayed.map((chip) => (
              <span
                key={chip.id}
                role="listitem"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
              >
                <svg
                  className="w-3.5 h-3.5 text-amber-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {chip.label}
                {chip.rationale && <RationaleTooltip text={chip.rationale} />}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Bottom line callout */}
      <div className="pt-4 border-t border-slate-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
              Bottom Line
            </p>
            <p className="text-[13px] text-slate-800 font-medium leading-relaxed bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              {bottomLine}
            </p>
          </div>
          <div className="pt-5">
            <BottomLineCopyButton text={bottomLine} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COVERAGE REQUIREMENTS - ACTIONABLE PREP PANEL
// 3 sections: Policy facts, Documentation checklist, Source
// ============================================

interface CoverageRequirementsPanelProps {
  caseData: AdaptedCase | Case | null;
  caseId: string;
  callMode?: boolean;
}

// Documentation checklist item
// Documentation checklist item
interface CoverageChecklistItem {
  id: string;
  label: string;
  category: "neuro" | "conservative" | "redflags" | "documentation";
  autoCheckPills?: string[];
}

// Standard documentation checklist items
const DOCUMENTATION_CHECKLIST: CoverageChecklistItem[] = [
  {
    id: "neuro_deficits",
    label: "Specific neurologic deficits documented",
    category: "neuro",
    autoCheckPills: ["weakness", "numbness", "reflex", "radiculopathy"],
  },
  {
    id: "conservative_dates",
    label: "Dates + duration of conservative treatments",
    category: "conservative",
    autoCheckPills: [
      "pt",
      "nsaids",
      "oral_steroids",
      "muscle_relaxants",
      "gabapentin",
      "epidural",
    ],
  },
  {
    id: "pt_notes",
    label: "PT notes or therapy documentation available",
    category: "documentation",
    autoCheckPills: ["pt"],
  },
  {
    id: "red_flags",
    label: "Red flag symptoms clearly stated",
    category: "redflags",
    autoCheckPills: ["bowel_bladder", "saddle", "progressive", "weakness"],
  },
];

// Generate coverage requirements based on insurer
function generateCoverageRequirements(
  caseData: AdaptedCase | Case | null
): string[] {
  const requirements: string[] = [];

  const insurer = caseData?.insurance_company?.toLowerCase() || "";
  const requestType = caseData?.request_type?.toLowerCase() || "";

  // Common requirements
  if (
    insurer.includes("evicore") ||
    insurer.includes("cigna") ||
    insurer.includes("anthem")
  ) {
    requirements.push("eviCore authorization required");
  } else if (insurer.includes("united") || insurer.includes("optum")) {
    requirements.push("Optum prior authorization pathway");
  } else if (insurer.includes("aetna")) {
    requirements.push("Aetna precertification required");
  }

  // Conservative therapy requirements
  if (requestType.includes("mri") || requestType.includes("imaging")) {
    requirements.push("≥4–6 weeks conservative therapy unless red flags");
    requirements.push("Physical therapy documentation expected");
  }

  // Medication-related
  if (requestType.includes("medication") || requestType.includes("rx")) {
    requirements.push("Step therapy documentation required");
    requirements.push("Prior medication trials documented");
  }

  // Procedure-related
  if (
    requestType.includes("procedure") ||
    requestType.includes("surgery") ||
    requestType.includes("injection")
  ) {
    requirements.push("Conservative treatment failure documented");
    requirements.push("Imaging correlation with symptoms required");
  }

  // Add generic if empty
  if (requirements.length === 0) {
    requirements.push("Prior authorization required");
    requirements.push("Medical necessity documentation needed");
  }

  return requirements;
}

// Generate source citation
function generateSourceCitation(caseData: AdaptedCase | Case | null): {
  text: string;
  url?: string;
} {
  const requestType = caseData?.request_type?.toLowerCase() || "";

  if (
    requestType.includes("mri") ||
    requestType.includes("lumbar") ||
    requestType.includes("spine")
  ) {
    return {
      text: "ACR Appropriateness Criteria for Low Back Pain",
      url: "https://www.acr.org/Clinical-Resources/ACR-Appropriateness-Criteria",
    };
  }

  if (
    requestType.includes("knee") ||
    requestType.includes("shoulder") ||
    requestType.includes("hip")
  ) {
    return {
      text: "ACR Appropriateness Criteria",
      url: "https://www.acr.org/Clinical-Resources/ACR-Appropriateness-Criteria",
    };
  }

  return { text: "Clinical guidelines and payer policies" };
}

function CoverageRequirementsPanel({
  caseData,
  caseId,
  callMode = false,
}: CoverageRequirementsPanelProps) {
  // Load clinical arguments to auto-check items
  const [clinicalArgs, setClinicalArgs] = useState<StoredClinicalArgs | null>(
    null
  );
  const [manualChecks, setManualChecks] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (caseId) {
      const storageKey = `peerprep.clinicalargs.v4:${caseId}`;
      try {
        const pillsRaw = localStorage.getItem(`${storageKey}:pills`);
        const extrasRaw = localStorage.getItem(`${storageKey}:extras`);

        if (pillsRaw || extrasRaw) {
          setClinicalArgs({
            pills: pillsRaw ? JSON.parse(pillsRaw) : {},
            extras: extrasRaw ? JSON.parse(extrasRaw) : {},
            other: {},
          });
        }
      } catch {
        // ignore
      }
    }
  }, [caseId]);

  // Listen for clinical args updates
  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.caseId === caseId) {
        const storageKey = `peerprep.clinicalargs.v4:${caseId}`;
        try {
          const pillsRaw = localStorage.getItem(`${storageKey}:pills`);
          const extrasRaw = localStorage.getItem(`${storageKey}:extras`);
          setClinicalArgs({
            pills: pillsRaw ? JSON.parse(pillsRaw) : {},
            extras: extrasRaw ? JSON.parse(extrasRaw) : {},
            other: {},
          });
        } catch {
          // ignore
        }
      }
    };

    window.addEventListener("clinicalArgsUpdated", handleUpdate);
    return () =>
      window.removeEventListener("clinicalArgsUpdated", handleUpdate);
  }, [caseId]);

  // Check if an item is satisfied by clinical arguments
  const isAutoChecked = useCallback(
    (item: CoverageChecklistItem): boolean => {
      if (!clinicalArgs || !item.autoCheckPills) return false;

      const allPills = Object.values(clinicalArgs.pills).flat();
      return item.autoCheckPills.some((pill) => allPills.includes(pill));
    },
    [clinicalArgs]
  );

  // Toggle manual check
  const toggleCheck = (itemId: string) => {
    setManualChecks((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const requirements = useMemo(
    () => generateCoverageRequirements(caseData),
    [caseData]
  );
  const source = useMemo(() => generateSourceCitation(caseData), [caseData]);

  // Call mode: compact
  if (callMode) {
    return (
      <div className="space-y-4">
        {/* Documentation Checklist - Primary focus in call mode */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-teal-400 font-semibold mb-2">
            Have Ready for Call
          </p>
          <div className="space-y-2">
            {DOCUMENTATION_CHECKLIST.map((item) => {
              const checked = isAutoChecked(item) || manualChecks.has(item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => toggleCheck(item.id)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                    checked
                      ? "bg-teal-900/30 border border-teal-700/50"
                      : "bg-slate-800/50 border border-slate-700/50 hover:border-slate-600"
                  }`}
                >
                  <span
                    className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                      checked
                        ? "bg-teal-500 border-teal-500"
                        : "border-slate-500"
                    }`}
                  >
                    {checked && <IconCheck className="w-3 h-3 text-white" />}
                  </span>
                  <span
                    className={`text-sm ${
                      checked ? "text-teal-200" : "text-slate-300"
                    }`}
                  >
                    {item.label}
                  </span>
                  {isAutoChecked(item) && !manualChecks.has(item.id) && (
                    <span className="text-[10px] text-teal-400 ml-auto">
                      Auto
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Normal mode: full 3-section layout
  return (
    <div className="space-y-6">
      {/* Section 1: Coverage Requirements */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
          What this insurer typically checks for approval
        </p>
        <ul className="space-y-1.5 mt-2">
          {requirements.map((req, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 text-[13px] text-slate-600"
            >
              <span className="text-slate-400 mt-0.5">•</span>
              <span>{req}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Section 2: Documentation Checklist */}
      <div className="pt-4 border-t border-slate-100">
        <p className="text-[11px] uppercase tracking-wider text-teal-700 font-semibold mb-3">
          Have Ready for Call
        </p>
        <div className="space-y-2">
          {DOCUMENTATION_CHECKLIST.map((item) => {
            const autoSatisfied = isAutoChecked(item);
            const manuallyChecked = manualChecks.has(item.id);
            const checked = autoSatisfied || manuallyChecked;

            return (
              <div
                key={item.id}
                onClick={() => toggleCheck(item.id)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all border ${
                  checked
                    ? "bg-teal-50 border-teal-200"
                    : "bg-slate-50 border-slate-200 hover:border-teal-300 hover:bg-teal-50/50"
                }`}
              >
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    checked ? "bg-teal-500 border-teal-500" : "border-slate-300"
                  }`}
                >
                  {checked && <IconCheck className="w-3.5 h-3.5 text-white" />}
                </span>
                <span
                  className={`text-[13px] font-medium ${
                    checked ? "text-teal-800" : "text-slate-700"
                  }`}
                >
                  {item.label}
                </span>
                {autoSatisfied && !manuallyChecked && (
                  <span className="ml-auto text-[10px] text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded">
                    From Clinical Args
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 3: Source */}
      <div className="pt-3 border-t border-slate-100">
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
          <span>Source:</span>
          {source.url ? (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-teal-600 hover:underline inline-flex items-center gap-1"
            >
              {source.text}
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          ) : (
            <span className="text-slate-500">{source.text}</span>
          )}
        </p>
      </div>
    </div>
  );
}

// ============================================
// QUICK REFERENCE - PEER-TO-PEER CUE CARD
// 10-15 second scannable summary for live calls
// ============================================

interface QuickRefData {
  clinicalRationale: string;
  keyFacts: string[];
  theAsk: string;
}

// Structure matching Clinical Arguments storage
interface StoredClinicalArgs {
  pills: Record<string, string[]>;
  extras: Record<string, Record<string, string>>;
  other: Record<string, Record<string, string>>;
}

// Load Clinical Arguments from localStorage
function loadClinicalArgsFromStorage(
  caseId: string
): StoredClinicalArgs | null {
  const storageKey = `peerprep.clinicalargs.v4:${caseId}`;
  try {
    const pillsRaw = localStorage.getItem(`${storageKey}:pills`);
    const extrasRaw = localStorage.getItem(`${storageKey}:extras`);
    const otherRaw = localStorage.getItem(`${storageKey}:other`);

    if (!pillsRaw && !extrasRaw) return null;

    return {
      pills: pillsRaw ? JSON.parse(pillsRaw) : {},
      extras: extrasRaw ? JSON.parse(extrasRaw) : {},
      other: otherRaw ? JSON.parse(otherRaw) : {},
    };
  } catch {
    return null;
  }
}

function extractQuickRefData(
  caseData: Case | null,
  clinicalArgs: StoredClinicalArgs | null
): QuickRefData {
  const data: QuickRefData = {
    clinicalRationale: "",
    keyFacts: [],
    theAsk: "",
  };

  if (!caseData) return data;

  // === PRIMARY SOURCE: Clinical Arguments from localStorage ===
  const hasClinicaArgs =
    clinicalArgs &&
    Object.values(clinicalArgs.pills).some((arr) => arr.length > 0);

  // Debug logging
  if (typeof window !== "undefined") {
    console.log("[QuickRef] Clinical Arguments loaded:", {
      hasClinicaArgs,
      pills: clinicalArgs?.pills,
      extras: clinicalArgs?.extras,
    });
  }

  // Extract structured data from Clinical Arguments
  let findings: string[] = [];
  let failedTreatments: string[] = [];
  let durationText = "";
  let painScore = "";
  let hasRedFlags = false;
  let hasProgression = false;

  if (hasClinicaArgs && clinicalArgs) {
    // Neuro Red Flags - pill IDs match talkingPointsEngine categories
    const neuroPills = clinicalArgs.pills["neuroRedFlags"] || [];
    if (neuroPills.includes("weakness")) findings.push("motor weakness");
    if (neuroPills.includes("numbness")) findings.push("sensory changes");
    if (neuroPills.includes("reflex")) findings.push("reflex changes");
    if (neuroPills.includes("bowel_bladder")) {
      hasRedFlags = true;
      findings.push("bowel/bladder symptoms");
    }
    if (neuroPills.includes("saddle")) {
      hasRedFlags = true;
      findings.push("saddle anesthesia");
    }
    if (neuroPills.includes("progressive"))
      findings.push("progressive deficit");
    if (neuroPills.includes("radiculopathy"))
      findings.push("radicular symptoms");

    // Progressive Deterioration - pill IDs match talkingPointsEngine categories
    const progPills = clinicalArgs.pills["progressiveDeterioration"] || [];
    if (progPills.length > 0) {
      hasProgression = true;
      if (progPills.includes("pain_worsening")) findings.push("worsening pain");
      if (progPills.includes("function_decline"))
        findings.push("functional decline");
      if (progPills.includes("adl_impact")) findings.push("ADL limitations");
      if (progPills.includes("work_limited")) findings.push("work limitations");
      if (progPills.includes("sleep_disturbed"))
        findings.push("sleep disturbance");
      if (progPills.includes("mobility_reduced"))
        findings.push("reduced mobility");
    }
    const progExtras = clinicalArgs.extras["progressiveDeterioration"] || {};
    if (progExtras["painFrom"] && progExtras["painTo"]) {
      painScore = `${progExtras["painFrom"]}→${progExtras["painTo"]}/10`;
    }

    // Failed Conservative Therapy - pill IDs match talkingPointsEngine categories
    const failedPills = clinicalArgs.pills["failedConservativeTx"] || [];
    if (failedPills.includes("pt")) failedTreatments.push("PT");
    if (failedPills.includes("nsaids")) failedTreatments.push("NSAIDs");
    if (failedPills.includes("oral_steroids"))
      failedTreatments.push("steroids");
    if (failedPills.includes("muscle_relaxants"))
      failedTreatments.push("muscle relaxants");
    if (failedPills.includes("gabapentin")) failedTreatments.push("gabapentin");
    if (failedPills.includes("epidural"))
      failedTreatments.push("epidural injections");
    if (failedPills.includes("activity_mod"))
      failedTreatments.push("activity modification");
    if (failedPills.includes("hep")) failedTreatments.push("home exercise");

    const failedExtras = clinicalArgs.extras["failedConservativeTx"] || {};
    if (failedExtras["durationWeeks"]) {
      durationText = `${failedExtras["durationWeeks"]} weeks`;
    }

    // Treatment Planning - pill IDs match talkingPointsEngine categories
    // These indicate why imaging is needed but don't add to findings directly

    // Differential Diagnosis - pill IDs match talkingPointsEngine categories
    const diffPills = clinicalArgs.pills["differentialDiagnosis"] || [];
    if (diffPills.includes("herniation"))
      findings.push("suspected disc herniation");
    if (diffPills.includes("stenosis")) findings.push("suspected stenosis");
    if (diffPills.includes("nerve_compression"))
      findings.push("nerve compression");
    if (diffPills.includes("cauda_equina")) {
      hasRedFlags = true;
      findings.push("cauda equina concern");
    }
    if (diffPills.includes("tumor")) {
      hasRedFlags = true;
      findings.push("concern for tumor");
    }
    if (diffPills.includes("infection")) {
      hasRedFlags = true;
      findings.push("concern for infection");
    }
    if (diffPills.includes("fracture")) {
      hasRedFlags = true;
      findings.push("concern for fracture");
    }
  }

  // === FALLBACK: Extract from case text if no Clinical Arguments ===
  if (!hasClinicaArgs) {
    const ctx = (caseData.additional_context || "").toLowerCase();
    const tried = (caseData.tried_already || "").toLowerCase();
    const details = (caseData.request_details || "").toLowerCase();
    const history = (caseData.clinical_history || "").toLowerCase();
    const allText = `${ctx} ${tried} ${details} ${history}`;

    // Findings from text
    if (ctx.includes("weakness") || allText.includes("motor deficit"))
      findings.push("weakness");
    if (ctx.includes("numbness") || ctx.includes("sensory loss"))
      findings.push("sensory changes");
    if (ctx.includes("radiculopathy") || ctx.includes("radicular"))
      findings.push("radicular symptoms");
    if (ctx.includes("bowel") || ctx.includes("bladder")) {
      hasRedFlags = true;
      findings.push("bowel/bladder symptoms");
    }

    // Progression
    hasProgression =
      ctx.includes("worsening") ||
      ctx.includes("progressive") ||
      ctx.includes("deteriorat");

    // Duration
    const durationMatch = allText.match(/(\d+)\s*(weeks?|wks?|months?|mos?)/i);
    if (durationMatch) {
      const unit = durationMatch[2].toLowerCase().startsWith("m")
        ? "months"
        : "weeks";
      durationText = `${durationMatch[1]} ${unit}`;
    }

    // Failed treatments from text
    if (tried.includes("pt") || tried.includes("physical therapy"))
      failedTreatments.push("PT");
    if (tried.includes("nsaid")) failedTreatments.push("NSAIDs");
    if (tried.includes("steroid")) failedTreatments.push("steroids");
    if (tried.includes("injection")) failedTreatments.push("injections");

    // Pain score
    const painMatch = allText.match(/pain[:\s]*(\d+)\s*[\/\\]?\s*10/i);
    if (painMatch) painScore = `${painMatch[1]}/10`;
  }

  // Debug logging
  if (typeof window !== "undefined") {
    console.log("[QuickRef] Extracted data:", {
      findings,
      failedTreatments,
      durationText,
      painScore,
      hasRedFlags,
      hasProgression,
    });
  }

  // Dedupe findings
  findings = [...new Set(findings)];

  // === Determine study type ===
  const requestLower = (caseData.request_details || "").toLowerCase();
  let studyName = "MRI";
  if (requestLower.includes("ct ") || requestLower.includes("ct scan"))
    studyName = "CT";
  if (requestLower.includes("x-ray") || requestLower.includes("xray"))
    studyName = "X-ray";

  // === SECTION 1: Clinical Rationale ===
  if (hasRedFlags && findings.length > 0) {
    const topFindings = findings.slice(0, 2).join(" and ");
    data.clinicalRationale = `${studyName} is urgent due to ${topFindings}.`;
  } else if (
    findings.length > 0 &&
    durationText &&
    failedTreatments.length > 0
  ) {
    data.clinicalRationale = `${studyName} is indicated for ${
      findings[0]
    } persisting ${durationText} despite ${failedTreatments
      .slice(0, 2)
      .join(" and ")}.`;
  } else if (findings.length > 0 && failedTreatments.length > 0) {
    data.clinicalRationale = `${studyName} is indicated for ${
      findings[0]
    } after failed ${failedTreatments.slice(0, 2).join(" and ")}.`;
  } else if (findings.length > 0 && durationText) {
    data.clinicalRationale = `${studyName} is indicated for ${findings[0]} persisting for ${durationText}.`;
  } else if (hasProgression && durationText) {
    data.clinicalRationale = `${studyName} is indicated for progressive symptoms over ${durationText}.`;
  } else if (failedTreatments.length > 0 && durationText) {
    data.clinicalRationale = `${studyName} is indicated after ${durationText} of failed ${failedTreatments
      .slice(0, 2)
      .join(" and ")}.`;
  } else if (findings.length > 0) {
    data.clinicalRationale = `${studyName} is indicated due to ${findings
      .slice(0, 2)
      .join(" and ")} on exam.`;
  } else if (failedTreatments.length > 0) {
    data.clinicalRationale = `${studyName} is indicated after failed ${failedTreatments.join(
      " and "
    )}.`;
  }

  // === SECTION 2: Key Facts (max 4) ===
  if (painScore && durationText) {
    data.keyFacts.push(`Pain ${painScore} × ${durationText}`);
  } else if (painScore) {
    data.keyFacts.push(`Pain ${painScore}`);
  } else if (durationText) {
    data.keyFacts.push(`${durationText} duration`);
  }

  if (failedTreatments.length > 0) {
    data.keyFacts.push(`Failed ${failedTreatments.slice(0, 3).join(" + ")}`);
  }

  if (findings.length > 0) {
    const findingStr = findings
      .slice(0, 2)
      .map((f, i) => (i === 0 ? f.charAt(0).toUpperCase() + f.slice(1) : f))
      .join(" + ");
    data.keyFacts.push(findingStr);
  }

  if (
    hasProgression &&
    !data.keyFacts.some((f) => f.toLowerCase().includes("progress"))
  ) {
    data.keyFacts.push("Progressive decline");
  }

  if (
    hasRedFlags &&
    !data.keyFacts.some(
      (f) =>
        f.toLowerCase().includes("red flag") ||
        f.toLowerCase().includes("bowel") ||
        f.toLowerCase().includes("bladder")
    )
  ) {
    data.keyFacts.push("Red flag symptoms");
  }

  data.keyFacts = data.keyFacts.slice(0, 4);

  // === SECTION 3: The Ask (PROCEDURE-FIRST FORMAT) ===
  // Format: "[Procedure] is medically necessary due to [clinical drivers]."
  // Under 25 words, declarative clinical language for peer-to-peer calls.

  // Extract and normalize study/procedure name
  let studyRequest = caseData.request_details?.split(/[,;]/)[0]?.trim() || "";
  studyRequest = studyRequest
    .replace(/^study:\s*/i, "")
    .replace(/^request(ing)?:\s*/i, "")
    .replace(/^for\s+/i, "")
    .trim();

  // Normalize MRI/CT capitalization
  if (studyRequest.toLowerCase().startsWith("mri")) {
    studyRequest = "MRI " + studyRequest.slice(4).toLowerCase().trim();
  } else if (studyRequest.toLowerCase().startsWith("ct")) {
    studyRequest = "CT " + studyRequest.slice(2).toLowerCase().trim();
  }

  // Clean up contrast notation
  studyRequest = studyRequest.replace(
    /\s*\(without contrast\)/i,
    " without contrast"
  );
  studyRequest = studyRequest.replace(
    /\s*\(with contrast\)/i,
    " with contrast"
  );
  studyRequest = studyRequest.replace(/[.,;:]+$/, "").trim();

  // Build clinical drivers string (prioritized, concise)
  let clinicalDrivers = "";

  if (hasRedFlags && findings.length > 0) {
    // Red flags take priority - urgent clinical concern
    const redFlagTerms = findings.filter(
      (f) =>
        f.includes("bowel") ||
        f.includes("bladder") ||
        f.includes("saddle") ||
        f.includes("cauda equina") ||
        f.includes("tumor") ||
        f.includes("infection") ||
        f.includes("fracture")
    );
    const otherFindings = findings.filter((f) => !redFlagTerms.includes(f));

    if (redFlagTerms.length > 0 && otherFindings.length > 0) {
      clinicalDrivers = `${otherFindings[0]} with ${redFlagTerms[0]}`;
    } else if (redFlagTerms.length > 0) {
      clinicalDrivers = redFlagTerms.slice(0, 2).join(" and ");
    } else {
      clinicalDrivers = findings.slice(0, 2).join(" and ");
    }
  } else if (
    findings.length > 0 &&
    failedTreatments.length > 0 &&
    durationText
  ) {
    // Findings + failed therapy + duration: most complete picture
    clinicalDrivers = `${
      findings[0]
    } despite ${durationText} of ${failedTreatments.slice(0, 2).join(" and ")}`;
  } else if (findings.length > 0 && failedTreatments.length > 0) {
    // Findings + failed therapy
    clinicalDrivers = `${findings[0]} despite ${failedTreatments
      .slice(0, 2)
      .join(" and ")}`;
  } else if (findings.length > 0 && durationText) {
    // Findings + duration
    clinicalDrivers = `persistent ${findings[0]} over ${durationText}`;
  } else if (hasProgression && durationText) {
    // Progressive symptoms
    clinicalDrivers = `progressive symptoms over ${durationText}`;
  } else if (failedTreatments.length > 0 && durationText) {
    // Failed therapy + duration (no specific findings)
    clinicalDrivers = `persistent symptoms despite ${durationText} of ${failedTreatments
      .slice(0, 2)
      .join(" and ")}`;
  } else if (findings.length >= 2) {
    // Multiple findings
    clinicalDrivers = findings.slice(0, 2).join(" and ");
  } else if (findings.length === 1) {
    // Single finding
    clinicalDrivers = findings[0];
  } else if (failedTreatments.length > 0) {
    // Failed therapy alone
    clinicalDrivers = `failed ${failedTreatments.slice(0, 2).join(" and ")}`;
  } else if (hasProgression) {
    clinicalDrivers = "progressive clinical deterioration";
  }

  // Build final Ask: procedure-first format
  if (studyRequest) {
    if (clinicalDrivers) {
      data.theAsk = `${studyRequest} is medically necessary due to ${clinicalDrivers}.`;
    } else {
      data.theAsk = `${studyRequest} is medically necessary to guide treatment planning.`;
    }
  }

  // Debug logging
  if (typeof window !== "undefined") {
    console.log("[QuickRef] Generated output:", {
      clinicalRationale: data.clinicalRationale,
      keyFacts: data.keyFacts,
      theAsk: data.theAsk,
      clinicalDrivers,
    });
  }

  return data;
}

// Main component
function KeyTalkingPointsSection({
  content,
  caseId,
  callMode,
  fontSize,
  caseData: caseProp,
  onClinicalArgumentsChange,
  initialClinicalArguments,
}: {
  content: string;
  caseId: string;
  callMode: boolean;
  fontSize: FontSize;
  caseData?: Case;
  onClinicalArgumentsChange?: (data: ClinicalArgumentsData) => void;
  initialClinicalArguments?: ClinicalArgumentsData | null;
}) {
  const storageKey = `peerprep.clinicalargs.v4:${caseId}`;

  // ── Dynamic categories: AI-first with hardcoded fallback ──
  const [dynamicCategories, setDynamicCategories] = useState<
    TalkingPointCategory[]
  >([]);
  const [classification, setClassification] =
    useState<CaseClassification | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSource, setAiSource] = useState<"ai" | "local" | null>(null);
  const [aiAutoSelected, setAiAutoSelected] = useState<Record<
    string,
    string[]
  > | null>(null);

  // Ref to track the case ID we're loading for (prevents stale responses)
  const aiLoadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!caseProp) {
      setDynamicCategories([]);
      setClassification(null);
      setAiSource(null);
      return;
    }

    const thisCaseId = caseProp.id;

    // 1. Instant fallback from hardcoded engine
    const fallback = buildTalkingPointCategories(caseProp);
    setDynamicCategories(fallback.categories);
    setClassification(fallback.classification);
    setAiSource("local");

    // 2. Try AI upgrade in background
    aiLoadRef.current = thisCaseId;
    setAiLoading(true);

    fetchAITalkingPointsCached(caseProp)
      .then((result) => {
        // Only apply if we're still on the same case
        if (aiLoadRef.current !== thisCaseId) return;

        if (result.categories.length > 0) {
          setDynamicCategories(result.categories);
          setAiAutoSelected(result.autoSelected);
          setAiSource("ai");
        }
      })
      .catch((err) => {
        console.warn(
          "[AI Talking Points] Failed, using local engine:",
          err.message
        );
      })
      .finally(() => {
        if (aiLoadRef.current === thisCaseId) {
          setAiLoading(false);
        }
      });
  }, [caseProp]);

  // Track which rows are expanded
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => {
    const autoExpand = dynamicCategories.find((c) => c.autoExpand);
    return autoExpand ? new Set([autoExpand.id]) : new Set();
  });

  // Update expanded rows when categories change (new case loaded)
  useEffect(() => {
    const autoExpand = dynamicCategories.find((c) => c.autoExpand);
    if (autoExpand) {
      setExpandedRows((prev) => {
        if (prev.size === 0) return new Set([autoExpand.id]);
        return prev;
      });
    }
  }, [dynamicCategories]);

  // Modal state for suggest from note
  const [showSuggestModal, setShowSuggestModal] = useState(false);

  // ── Selected pills state ────────────────────────────────
  const [selectedPills, setSelectedPills] = useState<
    Record<string, Set<string>>
  >(() => {
    // First try to load from initialClinicalArguments (DB)
    if (initialClinicalArguments?.selected) {
      const result: Record<string, Set<string>> = {};
      for (const [key, val] of Object.entries(
        initialClinicalArguments.selected
      )) {
        if (val.enabled && val.tags) {
          result[key] = new Set(val.tags);
        }
      }
      return result;
    }
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem(`${storageKey}:pills`);
      if (stored) {
        const parsed = JSON.parse(stored);
        const result: Record<string, Set<string>> = {};
        for (const [key, arr] of Object.entries(parsed)) {
          result[key] = new Set(arr as string[]);
        }
        return result;
      }
    } catch {
      /* ignore */
    }
    return {};
  });

  // ── "Other" text values ─────────────────────────────────
  const [otherValues, setOtherValues] = useState<
    Record<string, Record<string, string>>
  >(() => {
    if (initialClinicalArguments?.selected) {
      const result: Record<string, Record<string, string>> = {};
      for (const [key, val] of Object.entries(
        initialClinicalArguments.selected
      )) {
        if (val.other) {
          result[key] = { other: val.other };
        }
      }
      return result;
    }
    try {
      const stored = localStorage.getItem(`${storageKey}:other`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // ── Extra field values ──────────────────────────────────
  const [extraValues, setExtraValues] = useState<
    Record<string, Record<string, string>>
  >(() => {
    if (initialClinicalArguments?.selected) {
      const result: Record<string, Record<string, string>> = {};
      for (const [key, val] of Object.entries(
        initialClinicalArguments.selected
      )) {
        if (val.extras && Object.keys(val.extras).length > 0) {
          result[key] = val.extras;
        }
      }
      return result;
    }
    try {
      const stored = localStorage.getItem(`${storageKey}:extras`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // ── Auto-select pills: prefer AI selections, fall back to keyword engine ──
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [appliedAiSelections, setAppliedAiSelections] = useState(false);

  useEffect(() => {
    if (!caseProp || dynamicCategories.length === 0) return;

    // Skip if user already has manual selections from DB/localStorage
    const hasExistingSelections = Object.values(selectedPills).some(
      (s) => s.size > 0
    );

    // Case 1: AI selections arrived — apply them (upgrades local auto-select)
    if (aiAutoSelected && !appliedAiSelections && aiSource === "ai") {
      if (!hasExistingSelections || !hasAutoSelected) {
        const asSet: Record<string, Set<string>> = {};
        for (const [key, arr] of Object.entries(aiAutoSelected)) {
          asSet[key] = new Set(arr);
        }
        setSelectedPills(asSet);
        // Auto-expand categories with selections
        setExpandedRows((prev) => {
          const next = new Set(prev);
          for (const key of Object.keys(aiAutoSelected)) next.add(key);
          return next;
        });
      }
      setAppliedAiSelections(true);
      setHasAutoSelected(true);
      return;
    }

    // Case 2: Local engine auto-select (immediate, before AI returns)
    if (hasAutoSelected) return;
    if (hasExistingSelections) {
      setHasAutoSelected(true);
      return;
    }

    const auto = getAutoSelectedPills(dynamicCategories, caseProp);
    if (Object.keys(auto).length > 0) {
      const asSet: Record<string, Set<string>> = {};
      for (const [key, arr] of Object.entries(auto)) {
        asSet[key] = new Set(arr);
      }
      setSelectedPills(asSet);
    }
    setHasAutoSelected(true);
  }, [
    caseProp,
    dynamicCategories,
    hasAutoSelected,
    selectedPills,
    aiAutoSelected,
    aiSource,
    appliedAiSelections,
  ]);

  // ── Validate stored pills against current categories ────
  // (Prevents stale data when categories change)
  useEffect(() => {
    if (dynamicCategories.length === 0) return;

    const validCatIds = new Set(dynamicCategories.map((c) => c.id));
    const validPillIds = new Map<string, Set<string>>();
    for (const cat of dynamicCategories) {
      validPillIds.set(cat.id, new Set(cat.pills.map((p) => p.id)));
    }

    setSelectedPills((prev) => {
      let changed = false;
      const cleaned: Record<string, Set<string>> = {};
      for (const [catId, pills] of Object.entries(prev)) {
        if (!validCatIds.has(catId)) {
          changed = true;
          continue; // Drop entire category
        }
        const validIds = validPillIds.get(catId)!;
        const cleanedPills = new Set<string>();
        for (const pillId of pills) {
          if (validIds.has(pillId)) {
            cleanedPills.add(pillId);
          } else {
            changed = true;
          }
        }
        if (cleanedPills.size > 0) {
          cleaned[catId] = cleanedPills;
        }
      }
      return changed ? cleaned : prev;
    });
  }, [dynamicCategories]);

  // ── Debounced save to parent ────────────────────────────
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const saveToParent = useCallback(() => {
    if (!onClinicalArgumentsChange) return;

    const data: ClinicalArgumentsData = {
      selected: {},
      classification: classification
        ? {
            serviceType: classification.serviceType,
            specialtyDomain: classification.specialtyDomain,
          }
        : undefined,
      lastUpdatedAt: new Date().toISOString(),
    };

    for (const cat of dynamicCategories) {
      const pills = selectedPills[cat.id];
      const hasPills = pills && pills.size > 0;
      data.selected[cat.id] = {
        enabled: hasPills || false,
        tags: hasPills ? [...pills] : [],
        extras: extraValues[cat.id] || undefined,
        other: otherValues[cat.id]?.["other"] || undefined,
      };
    }

    onClinicalArgumentsChange(data);
  }, [
    selectedPills,
    extraValues,
    otherValues,
    onClinicalArgumentsChange,
    dynamicCategories,
    classification,
  ]);

  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveToParent(), 500);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [selectedPills, extraValues, otherValues, saveToParent]);

  // ── Persist to localStorage ─────────────────────────────
  useEffect(() => {
    try {
      const toStore: Record<string, string[]> = {};
      for (const [key, set] of Object.entries(selectedPills)) {
        toStore[key] = [...set];
      }
      localStorage.setItem(`${storageKey}:pills`, JSON.stringify(toStore));
      window.dispatchEvent(
        new CustomEvent("clinicalArgsUpdated", { detail: { caseId } })
      );
    } catch {
      /* ignore */
    }
  }, [selectedPills, storageKey, caseId]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}:other`, JSON.stringify(otherValues));
      window.dispatchEvent(
        new CustomEvent("clinicalArgsUpdated", { detail: { caseId } })
      );
    } catch {
      /* ignore */
    }
  }, [otherValues, storageKey, caseId]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}:extras`, JSON.stringify(extraValues));
      window.dispatchEvent(
        new CustomEvent("clinicalArgsUpdated", { detail: { caseId } })
      );
    } catch {
      /* ignore */
    }
  }, [extraValues, storageKey, caseId]);

  // ── Handlers ────────────────────────────────────────────
  const handleTogglePill = useCallback((rowId: string, pillId: string) => {
    setSelectedPills((prev) => {
      const current = prev[rowId] || new Set();
      const next = new Set(current);
      if (next.has(pillId)) next.delete(pillId);
      else next.add(pillId);
      return { ...prev, [rowId]: next };
    });
  }, []);

  const handleOtherChange = useCallback(
    (rowId: string, pillId: string, value: string) => {
      setOtherValues((prev) => ({
        ...prev,
        [rowId]: { ...(prev[rowId] || {}), [pillId]: value },
      }));
    },
    []
  );

  const handleExtraChange = useCallback(
    (rowId: string, fieldId: string, value: string) => {
      setExtraValues((prev) => ({
        ...prev,
        [rowId]: { ...(prev[rowId] || {}), [fieldId]: value },
      }));
    },
    []
  );

  const handleToggleExpand = useCallback((rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setSelectedPills({});
    setOtherValues({});
    setExtraValues({});
    setExpandedRows(new Set());
  }, []);

  // ── Handle applying suggestions from note analysis ──────
  // Maps suggestion tags to pill IDs across dynamic categories
  const handleApplySuggestions = useCallback(
    (
      newSelectedPills: Record<string, Set<string>>,
      newExtraValues: Record<string, Record<string, string>>,
      newOtherValues: Record<string, Record<string, string>>
    ) => {
      setSelectedPills((prev) => {
        const merged: Record<string, Set<string>> = { ...prev };
        for (const [category, pills] of Object.entries(newSelectedPills)) {
          const catExists = dynamicCategories.some((c) => c.id === category);
          if (catExists) {
            merged[category] = new Set([...(prev[category] || []), ...pills]);
          }
        }
        return merged;
      });

      setExtraValues((prev) => {
        const merged: Record<string, Record<string, string>> = { ...prev };
        for (const [category, values] of Object.entries(newExtraValues)) {
          const catExists = dynamicCategories.some((c) => c.id === category);
          if (catExists) {
            merged[category] = { ...(prev[category] || {}), ...values };
          }
        }
        return merged;
      });

      setOtherValues((prev) => {
        const merged: Record<string, Record<string, string>> = { ...prev };
        for (const [category, values] of Object.entries(newOtherValues)) {
          const catExists = dynamicCategories.some((c) => c.id === category);
          if (catExists) {
            merged[category] = { ...(prev[category] || {}), ...values };
          }
        }
        return merged;
      });

      // Expand rows with new selections
      setExpandedRows((prev) => {
        const next = new Set(prev);
        for (const [category, pills] of Object.entries(newSelectedPills)) {
          if (
            pills.size > 0 &&
            dynamicCategories.some((c) => c.id === category)
          ) {
            next.add(category);
          }
        }
        return next;
      });
    },
    [dynamicCategories]
  );

  // ── Computed values ─────────────────────────────────────
  const completedCount = dynamicCategories.filter((config) => {
    const pills = selectedPills[config.id];
    return pills && pills.size > 0;
  }).length;

  // Generate structured talking points via engine
  const structuredBullets = useMemo(() => {
    if (dynamicCategories.length === 0) return [];

    // Convert Set-based pills to string[] for engine
    const pillsAsArrays: Record<string, string[]> = {};
    for (const [key, set] of Object.entries(selectedPills)) {
      pillsAsArrays[key] = [...set];
    }

    // Convert other values to flat map
    const otherFlat: Record<string, string> = {};
    for (const [key, obj] of Object.entries(otherValues)) {
      if (obj?.["other"]) otherFlat[key] = obj["other"];
    }

    const summary = buildSpeakableSummary(
      dynamicCategories,
      pillsAsArrays,
      extraValues,
      otherFlat
    );

    if (!summary) return [];

    // Output is newline-separated clean sentences (each ending with ".")
    return summary
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }, [selectedPills, extraValues, otherValues, dynamicCategories]);

  // Export text for copy button
  const exportText = useMemo(() => {
    const lines = dynamicCategories.map((config) => {
      const pills = selectedPills[config.id] || new Set();
      const others = otherValues[config.id] || {};

      const selectedLabels = config.pills
        .filter((p) => pills.has(p.id))
        .map((p) =>
          p.isOther && others[p.id] ? `Other: ${others[p.id]}` : p.label
        );

      const hasContent = selectedLabels.length > 0;
      return `[${hasContent ? "x" : " "}] ${config.label}${
        hasContent ? ": " + selectedLabels.join(", ") : ""
      }`;
    });

    if (structuredBullets.length > 0) {
      lines.push(
        "",
        "--- Structured Talking Points ---",
        ...structuredBullets.map((b) => `• ${b}`)
      );
    }

    return lines.join("\n");
  }, [selectedPills, otherValues, structuredBullets, dynamicCategories]);

  // Show suggest-from-note only when compatible categories are present
  const suggestFromNoteAvailable = dynamicCategories.some(
    (c) =>
      c.id === "neuroSpecific" ||
      c.id === "mskSpecific" ||
      c.id === "failedConservativeTx" ||
      c.id === "ruleOutPathology" ||
      c.id.includes("neuro") ||
      c.id.includes("msk") ||
      c.id.includes("failedConservative")
  );

  // ── No categories fallback ──────────────────────────────
  if (dynamicCategories.length === 0) {
    // Show AI-generated content as-is when no case data
    return (
      <div className="space-y-3">
        {content && (
          <div className="rounded border border-slate-200 bg-white overflow-hidden">
            <div className="px-3 py-2 bg-slate-100 border-b border-slate-200">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Talking Points
              </span>
            </div>
            <div className="p-3 text-sm text-slate-600 whitespace-pre-wrap">
              {content}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Call mode ───────────────────────────────────────────
  if (callMode) {
    return (
      <div className="rounded border border-slate-700 bg-slate-800/50 overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-400">
              Clinical Arguments ({completedCount}/{dynamicCategories.length})
            </span>
            {classification && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">
                {classification.specialtyDomain.toUpperCase()}
              </span>
            )}
            {aiLoading && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-400 font-medium animate-pulse">
                ✨ Curating…
              </span>
            )}
            {aiSource === "ai" && !aiLoading && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400 font-medium">
                AI-curated
              </span>
            )}
          </div>
          <CopyButton text={exportText} callMode={true} />
        </div>
        <div className="divide-y divide-slate-700/50">
          {dynamicCategories.map((config) => {
            const pills = selectedPills[config.id] || new Set();
            const hasContent = pills.size > 0;
            return (
              <div
                key={config.id}
                className={`px-3 py-2 ${hasContent ? "bg-slate-700/30" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className={`w-3 h-3 rounded-full border flex-shrink-0 ${
                      hasContent
                        ? "border-slate-400 bg-slate-400"
                        : "border-slate-600"
                    }`}
                  />
                  <span
                    className={`text-xs font-medium ${
                      hasContent ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    {config.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 ml-5">
                  {config.pills
                    .filter((p) => !p.isOther)
                    .map((pill) => (
                      <button
                        key={pill.id}
                        onClick={() => handleTogglePill(config.id, pill.id)}
                        aria-pressed={pills.has(pill.id)}
                        className={`px-2 py-0.5 text-[11px] rounded-full border transition-all ${
                          pills.has(pill.id)
                            ? "bg-slate-500 text-white border-slate-500"
                            : "text-slate-400 border-slate-600 hover:border-slate-500"
                        }`}
                      >
                        {pill.label}
                      </button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
        <StructuredTalkingPoints bullets={structuredBullets} callMode={true} />
        <CriteriaMapping
          categories={dynamicCategories}
          selectedPills={selectedPills}
          classification={classification}
          callMode={true}
        />
      </div>
    );
  }

  // ── Normal mode ─────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Classification badge + AI indicator */}
      <div className="flex items-center gap-2 flex-wrap">
        {classification && (
          <>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 uppercase tracking-wider">
              {classification.serviceType.replace(/_/g, " ")}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 uppercase tracking-wider">
              {classification.specialtyDomain}
            </span>
          </>
        )}
        {aiLoading && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600 animate-pulse">
            ✨ Curating…
          </span>
        )}
        {aiSource === "ai" && !aiLoading && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600">
            AI-curated
          </span>
        )}
      </div>

      {/* 1. Structured Talking Points - PRIMARY output */}
      <StructuredTalkingPoints
        bullets={structuredBullets}
        callMode={false}
        showEmptyState={true}
      />

      {/* 2. Criteria Mapping - Auto-generated */}
      <CriteriaMapping
        categories={dynamicCategories}
        selectedPills={selectedPills}
        classification={classification}
        callMode={false}
      />

      {/* 3. Clinical Arguments - Interactive section */}
      <div className="rounded border border-slate-300 bg-white overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Clinical Arguments
            </span>
            <span className="text-[11px] text-slate-400">(optional)</span>
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${
                completedCount > 0
                  ? "bg-slate-600 text-white"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {completedCount}/{dynamicCategories.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {suggestFromNoteAvailable && (
              <button
                onClick={() => setShowSuggestModal(true)}
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Suggest from note
              </button>
            )}
            {completedCount > 0 && (
              <button
                onClick={handleReset}
                className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
              >
                Reset
              </button>
            )}
            <CopyButton text={exportText} callMode={false} />
          </div>
        </div>

        {/* Helper text */}
        <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100">
          <p className="text-[11px] text-slate-500 italic">
            Tap any that apply to strengthen your case. Categories are tailored
            to this request.
          </p>
        </div>

        {/* Dynamic rows from engine */}
        <div>
          {dynamicCategories.map((config, index) => (
            <ClinicalArgumentRow
              key={config.id}
              config={config}
              selectedPills={selectedPills[config.id] || new Set()}
              onTogglePill={(pillId) => handleTogglePill(config.id, pillId)}
              otherValues={otherValues[config.id] || {}}
              onOtherChange={(pillId, value) =>
                handleOtherChange(config.id, pillId, value)
              }
              extraValues={extraValues[config.id] || {}}
              onExtraChange={(fieldId, value) =>
                handleExtraChange(config.id, fieldId, value)
              }
              isExpanded={expandedRows.has(config.id)}
              onToggleExpand={() => handleToggleExpand(config.id)}
              isLast={index === dynamicCategories.length - 1}
            />
          ))}
        </div>
      </div>

      {/* Suggest from Note Modal */}
      <SuggestFromNoteModal
        isOpen={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        onApply={handleApplySuggestions}
      />
    </div>
  );
}

// ============================================
// SUGGEST FROM NOTE - MODAL & REVIEW COMPONENTS
// ============================================

// LLM extraction prompt for clinical note analysis
const NOTE_EXTRACTION_PROMPT = `You are a clinical documentation analyst. Extract structured clinical information from the provided note to populate a prior authorization preparation tool.

CRITICAL RULES:
1. ONLY extract information EXPLICITLY stated in the note
2. NEVER infer or assume information not present
3. Pay close attention to NEGATIONS: "denies weakness", "no bowel/bladder symptoms" = negated
4. If uncertain, mark status as "uncertain"
5. Provide verbatim evidence snippets (≤25 words each, max 2 per item)
6. Prefer under-calling over over-calling

NORMALIZATION:
- "PT"/"physical therapy" → "Physical therapy"
- "NSAID"/"ibuprofen"/"naproxen"/"Motrin"/"Advil" → "NSAIDs"
- "gabapentin"/"Neurontin"/"Lyrica"/"pregabalin" → "Gabapentin/pregabalin"
- "ESI"/"epidural steroid injection" → "Epidural injections"
- Pain scores like "8/10" → painFrom or painTo as appropriate
- Convert months to weeks (multiply by 4.3, round)

Return ONLY valid JSON matching this schema:
{
  "suggestions": {
    "neuroRedFlags": {
      "confidence": "high|medium|low",
      "recommend_apply": boolean,
      "tags": [{ "value": "Weakness|Numbness|Reflex changes|Bowel/bladder dysfunction|Saddle anesthesia|Progressive deficit|Severe radiculopathy|Other", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }],
      "onset": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] },
      "otherText": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }
    },
    "progressiveDeterioration": {
      "confidence": "high|medium|low",
      "recommend_apply": boolean,
      "tags": [{ "value": "Pain worsening|Function declining|ADL impact|Work limited|Sleep disturbed|Mobility reduced|Other", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }],
      "painFrom": { "value": "number|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] },
      "painTo": { "value": "number|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] },
      "durationWeeks": { "value": "number|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] },
      "otherText": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }
    },
    "failedConservativeTx": {
      "confidence": "high|medium|low",
      "recommend_apply": boolean,
      "tags": [{ "value": "NSAIDs|Physical therapy|Activity modification|Oral steroids|Epidural injections|Gabapentin/pregabalin|Muscle relaxants|Home exercise|Other", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }],
      "durationWeeks": { "value": "number|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] },
      "otherText": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }
    },
    "treatmentPlanning": {
      "confidence": "high|medium|low",
      "recommend_apply": boolean,
      "tags": [{ "value": "Surgery consult|Injection|Targeted therapy|Specialist referral|Rule out structural cause|Other", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }],
      "rationale": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] },
      "otherText": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }
    },
    "differentialDiagnosis": {
      "confidence": "high|medium|low",
      "recommend_apply": boolean,
      "tags": [{ "value": "Disc herniation|Spinal stenosis|Nerve compression|Fracture|Infection|Tumor/malignancy|Cauda equina|Other", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }],
      "findings": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] },
      "otherText": { "value": "string|null", "status": "affirmed|negated|uncertain", "evidence": ["snippet"] }
    }
  },
  "meta": {
    "note_type_guess": "ED note|clinic note|H&P|consult note|PT note|unknown",
    "warnings": ["string"]
  }
}`;

// Suggestion item component for review
function SuggestionItem({
  tag,
  isAccepted,
  onToggle,
}: {
  tag: TagSuggestion;
  isAccepted: boolean;
  onToggle: () => void;
}) {
  const statusColors = {
    affirmed: "bg-emerald-50 border-emerald-200 text-emerald-700",
    negated: "bg-red-50 border-red-200 text-red-600",
    uncertain: "bg-amber-50 border-amber-200 text-amber-700",
  };

  const statusLabels = {
    affirmed: "Found",
    negated: "Denied",
    uncertain: "Uncertain",
  };

  return (
    <div
      className={`flex items-start gap-2 p-2 rounded border ${
        isAccepted
          ? "bg-slate-50 border-slate-300"
          : "bg-white border-slate-200"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
          isAccepted
            ? "bg-slate-600 border-slate-600"
            : "border-slate-300 hover:border-slate-400"
        }`}
      >
        {isAccepted && (
          <svg
            className="w-2.5 h-2.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-700">
            {tag.value}
          </span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded border ${
              statusColors[tag.status]
            }`}
          >
            {statusLabels[tag.status]}
          </span>
        </div>
        {tag.evidence.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {tag.evidence.map((snippet, i) => (
              <p key={i} className="text-[11px] text-slate-500 italic">
                "{snippet}"
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Field suggestion display
function FieldSuggestionDisplay<T extends string | number>({
  label,
  field,
  suffix,
}: {
  label: string;
  field?: FieldSuggestion<T>;
  suffix?: string;
}) {
  if (!field || field.value === null) return null;

  const statusColors = {
    affirmed: "text-emerald-600",
    negated: "text-red-500",
    uncertain: "text-amber-600",
  };

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-500">{label}:</span>
      <span className={`font-medium ${statusColors[field.status]}`}>
        {String(field.value)}
        {suffix}
      </span>
      {field.evidence.length > 0 && (
        <span className="text-slate-400 italic truncate max-w-[200px]">
          "{field.evidence[0]}"
        </span>
      )}
    </div>
  );
}

// Category suggestion section
function CategorySuggestionSection({
  title,
  suggestion,
  acceptedTags,
  onToggleTag,
  children,
}: {
  title: string;
  suggestion: CategorySuggestion;
  acceptedTags: Set<string>;
  onToggleTag: (tagValue: string) => void;
  children?: React.ReactNode;
}) {
  const confidenceColors = {
    high: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-slate-100 text-slate-600",
  };

  const affirmedTags = suggestion.tags.filter((t) => t.status === "affirmed");
  const negatedTags = suggestion.tags.filter((t) => t.status === "negated");
  const uncertainTags = suggestion.tags.filter((t) => t.status === "uncertain");

  if (
    affirmedTags.length === 0 &&
    negatedTags.length === 0 &&
    uncertainTags.length === 0
  ) {
    return null;
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
              confidenceColors[suggestion.confidence]
            }`}
          >
            {suggestion.confidence} confidence
          </span>
          {suggestion.recommend_apply && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-medium">
              Recommended
            </span>
          )}
        </div>
      </div>
      <div className="p-3 space-y-2">
        {/* Affirmed tags */}
        {affirmedTags.length > 0 && (
          <div className="space-y-1.5">
            {affirmedTags.map((tag, i) => (
              <SuggestionItem
                key={i}
                tag={tag}
                isAccepted={acceptedTags.has(tag.value)}
                onToggle={() => onToggleTag(tag.value)}
              />
            ))}
          </div>
        )}
        {/* Negated tags - show as informational */}
        {negatedTags.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">
              Explicitly Denied
            </p>
            <div className="flex flex-wrap gap-1">
              {negatedTags.map((tag, i) => (
                <span
                  key={i}
                  className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-500 border border-red-100"
                >
                  ✗ {tag.value}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Uncertain tags */}
        {uncertainTags.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">
              Uncertain (Review Carefully)
            </p>
            <div className="space-y-1.5">
              {uncertainTags.map((tag, i) => (
                <SuggestionItem
                  key={i}
                  tag={tag}
                  isAccepted={acceptedTags.has(tag.value)}
                  onToggle={() => onToggleTag(tag.value)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Extra fields */}
        {children && (
          <div className="pt-2 border-t border-slate-100 space-y-1">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

// Main suggestion modal
function SuggestFromNoteModal({
  isOpen,
  onClose,
  onApply,
}: {
  isOpen: boolean;
  onClose: () => void;
  onApply: (
    selectedPills: Record<string, Set<string>>,
    extraValues: Record<string, Record<string, string>>,
    otherValues: Record<string, Record<string, string>>
  ) => void;
}) {
  const [step, setStep] = useState<"input" | "analyzing" | "review" | "error">(
    "input"
  );
  const [noteText, setNoteText] = useState("");
  const [includesPhi, setIncludesPhi] = useState(false);
  const [suggestions, setSuggestions] =
    useState<NoteSuggestionsResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Track accepted suggestions per category
  const [acceptedTags, setAcceptedTags] = useState<Record<string, Set<string>>>(
    {
      neuroRedFlags: new Set(),
      progressiveDeterioration: new Set(),
      failedConservativeTx: new Set(),
      treatmentPlanning: new Set(),
      differentialDiagnosis: new Set(),
    }
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep("input");
      setNoteText("");
      setIncludesPhi(false);
      setSuggestions(null);
      setErrorMessage("");
      setAcceptedTags({
        neuroRedFlags: new Set(),
        progressiveDeterioration: new Set(),
        failedConservativeTx: new Set(),
        treatmentPlanning: new Set(),
        differentialDiagnosis: new Set(),
      });
    }
  }, [isOpen]);

  // Analyze note using LLM
  const handleAnalyze = async () => {
    if (!noteText.trim() || noteText.trim().length < 50) {
      setErrorMessage(
        "Please provide a clinical note with at least 50 characters."
      );
      setStep("error");
      return;
    }

    setStep("analyzing");
    setErrorMessage("");

    try {
      // Call the LLM extraction endpoint
      const response = await fetch("/api/extract-clinical-arguments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          note: noteText,
          prompt: NOTE_EXTRACTION_PROMPT,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to analyze note");
      }

      const data = await response.json();

      // Validate response structure
      if (!data.suggestions) {
        throw new Error("Invalid response format");
      }

      setSuggestions(data);

      // Pre-select affirmed tags with high/medium confidence and recommend_apply
      const newAccepted: Record<string, Set<string>> = {
        neuroRedFlags: new Set(),
        progressiveDeterioration: new Set(),
        failedConservativeTx: new Set(),
        treatmentPlanning: new Set(),
        differentialDiagnosis: new Set(),
      };

      for (const [category, catSuggestion] of Object.entries(
        data.suggestions
      )) {
        if (category === "meta") continue;
        const suggestion = catSuggestion as CategorySuggestion;
        if (
          (suggestion.confidence === "high" ||
            suggestion.confidence === "medium") &&
          suggestion.recommend_apply
        ) {
          for (const tag of suggestion.tags) {
            if (tag.status === "affirmed") {
              newAccepted[category].add(tag.value);
            }
          }
        }
      }

      setAcceptedTags(newAccepted);
      setStep("review");
    } catch (error) {
      console.error("Note analysis error:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to analyze note. Please try again."
      );
      setStep("error");
    }
  };

  // Toggle tag acceptance
  const handleToggleTag = (category: string, tagValue: string) => {
    setAcceptedTags((prev) => {
      const current = prev[category] || new Set();
      const next = new Set(current);
      if (next.has(tagValue)) {
        next.delete(tagValue);
      } else {
        next.add(tagValue);
      }
      return { ...prev, [category]: next };
    });
  };

  // Apply accepted suggestions
  const handleApply = () => {
    if (!suggestions) return;

    const selectedPills: Record<string, Set<string>> = {};
    const extraValues: Record<string, Record<string, string>> = {};
    const otherValues: Record<string, Record<string, string>> = {};

    // Convert accepted tags to pill IDs
    for (const [category, tags] of Object.entries(acceptedTags)) {
      selectedPills[category] = new Set();
      for (const tagValue of tags) {
        const pillId = TAG_TO_PILL_ID[tagValue];
        if (pillId) {
          selectedPills[category].add(pillId);
        }
      }
    }

    // Extract extra field values from suggestions
    const s = suggestions.suggestions;

    // Neuro red flags
    if (
      s.neuroRedFlags.onset?.value &&
      s.neuroRedFlags.onset.status === "affirmed"
    ) {
      extraValues["neuroRedFlags"] = { onset: s.neuroRedFlags.onset.value };
    }
    if (
      s.neuroRedFlags.otherText?.value &&
      acceptedTags["neuroRedFlags"].has("Other")
    ) {
      otherValues["neuroRedFlags"] = { other: s.neuroRedFlags.otherText.value };
    }

    // Progressive deterioration
    if (acceptedTags["progressiveDeterioration"].size > 0) {
      const progExtras: Record<string, string> = {};
      if (
        s.progressiveDeterioration.painFrom?.value !== null &&
        s.progressiveDeterioration.painFrom?.status === "affirmed"
      ) {
        progExtras["painFrom"] = String(
          s.progressiveDeterioration.painFrom.value
        );
      }
      if (
        s.progressiveDeterioration.painTo?.value !== null &&
        s.progressiveDeterioration.painTo?.status === "affirmed"
      ) {
        progExtras["painTo"] = String(s.progressiveDeterioration.painTo.value);
      }
      if (
        s.progressiveDeterioration.durationWeeks?.value !== null &&
        s.progressiveDeterioration.durationWeeks?.status === "affirmed"
      ) {
        progExtras["durationWeeks"] = String(
          s.progressiveDeterioration.durationWeeks.value
        );
      }
      if (Object.keys(progExtras).length > 0) {
        extraValues["progressiveDeterioration"] = progExtras;
      }
    }

    // Failed conservative
    if (
      s.failedConservativeTx.durationWeeks?.value !== null &&
      s.failedConservativeTx.durationWeeks?.status === "affirmed"
    ) {
      extraValues["failedConservativeTx"] = {
        durationWeeks: String(s.failedConservativeTx.durationWeeks.value),
      };
    }
    if (
      s.failedConservativeTx.otherText?.value &&
      acceptedTags["failedConservativeTx"].has("Other")
    ) {
      otherValues["failedConservativeTx"] = {
        other: s.failedConservativeTx.otherText.value,
      };
    }

    // Treatment planning
    if (
      s.treatmentPlanning.rationale?.value &&
      s.treatmentPlanning.rationale.status === "affirmed"
    ) {
      extraValues["treatmentPlanning"] = {
        rationale: s.treatmentPlanning.rationale.value,
      };
    }
    if (
      s.treatmentPlanning.otherText?.value &&
      acceptedTags["treatmentPlanning"].has("Other")
    ) {
      otherValues["treatmentPlanning"] = {
        other: s.treatmentPlanning.otherText.value,
      };
    }

    // Differential diagnosis
    if (
      s.differentialDiagnosis.findings?.value &&
      s.differentialDiagnosis.findings.status === "affirmed"
    ) {
      extraValues["differentialDiagnosis"] = {
        findings: s.differentialDiagnosis.findings.value,
      };
    }
    if (
      s.differentialDiagnosis.otherText?.value &&
      acceptedTags["differentialDiagnosis"].has("Other")
    ) {
      otherValues["differentialDiagnosis"] = {
        other: s.differentialDiagnosis.otherText.value,
      };
    }

    onApply(selectedPills, extraValues, otherValues);
    onClose();
  };

  // Count total accepted
  const totalAccepted = Object.values(acceptedTags).reduce(
    (sum, set) => sum + set.size,
    0
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-slate-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-slate-800">
              {step === "input" && "Suggest from Clinical Note"}
              {step === "analyzing" && "Analyzing Note..."}
              {step === "review" && "Review Suggestions"}
              {step === "error" && "Analysis Error"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Input step */}
          {step === "input" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Paste Clinical Note
                </label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Paste the clinical note here (ED note, clinic note, H&P, consult note, PT note, etc.)..."
                  className="w-full h-64 px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500/20"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  {noteText.length} characters
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includesPhi}
                  onChange={(e) => setIncludesPhi(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
                />
                <span className="text-sm text-slate-600">
                  Note may include PHI (for future gating)
                </span>
              </label>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700">
                  <strong>Note:</strong> This tool extracts structured
                  information to help pre-populate clinical arguments. It will
                  ONLY suggest items explicitly supported by the note text. All
                  suggestions require your review before applying.
                </p>
              </div>
            </div>
          )}

          {/* Analyzing step */}
          {step === "analyzing" && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
              <p className="mt-4 text-sm text-slate-600">
                Analyzing clinical note...
              </p>
              <p className="mt-1 text-xs text-slate-400">
                This may take a few seconds
              </p>
            </div>
          )}

          {/* Error step */}
          {step === "error" && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="mt-4 text-sm text-slate-700 font-medium">
                Analysis Failed
              </p>
              <p className="mt-1 text-xs text-slate-500 text-center max-w-sm">
                {errorMessage}
              </p>
              <button
                onClick={() => setStep("input")}
                className="mt-4 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Review step */}
          {step === "review" && suggestions && (
            <div className="space-y-4">
              {/* Meta info */}
              {suggestions.meta && (
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span>
                    Note type:{" "}
                    <strong className="text-slate-600">
                      {suggestions.meta.note_type_guess}
                    </strong>
                  </span>
                  {suggestions.meta.warnings.length > 0 && (
                    <span className="text-amber-600">
                      ⚠ {suggestions.meta.warnings.length} warning(s)
                    </span>
                  )}
                </div>
              )}

              {/* Warnings */}
              {suggestions.meta?.warnings.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-medium text-amber-700 mb-1">
                    Warnings:
                  </p>
                  <ul className="text-xs text-amber-600 space-y-0.5">
                    {suggestions.meta.warnings.map((w, i) => (
                      <li key={i}>• {w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Category sections */}
              <CategorySuggestionSection
                title="Neurological Red Flags"
                suggestion={suggestions.suggestions.neuroRedFlags}
                acceptedTags={acceptedTags["neuroRedFlags"]}
                onToggleTag={(tag) => handleToggleTag("neuroRedFlags", tag)}
              >
                <FieldSuggestionDisplay
                  label="Onset"
                  field={suggestions.suggestions.neuroRedFlags.onset}
                />
              </CategorySuggestionSection>

              <CategorySuggestionSection
                title="Progressive Deterioration"
                suggestion={suggestions.suggestions.progressiveDeterioration}
                acceptedTags={acceptedTags["progressiveDeterioration"]}
                onToggleTag={(tag) =>
                  handleToggleTag("progressiveDeterioration", tag)
                }
              >
                <FieldSuggestionDisplay
                  label="Pain From"
                  field={
                    suggestions.suggestions.progressiveDeterioration.painFrom
                  }
                  suffix="/10"
                />
                <FieldSuggestionDisplay
                  label="Pain To"
                  field={
                    suggestions.suggestions.progressiveDeterioration.painTo
                  }
                  suffix="/10"
                />
                <FieldSuggestionDisplay
                  label="Duration"
                  field={
                    suggestions.suggestions.progressiveDeterioration
                      .durationWeeks
                  }
                  suffix=" weeks"
                />
              </CategorySuggestionSection>

              <CategorySuggestionSection
                title="Failed Conservative Treatment"
                suggestion={suggestions.suggestions.failedConservativeTx}
                acceptedTags={acceptedTags["failedConservativeTx"]}
                onToggleTag={(tag) =>
                  handleToggleTag("failedConservativeTx", tag)
                }
              >
                <FieldSuggestionDisplay
                  label="Duration"
                  field={
                    suggestions.suggestions.failedConservativeTx.durationWeeks
                  }
                  suffix=" weeks"
                />
              </CategorySuggestionSection>

              <CategorySuggestionSection
                title="Treatment Planning"
                suggestion={suggestions.suggestions.treatmentPlanning}
                acceptedTags={acceptedTags["treatmentPlanning"]}
                onToggleTag={(tag) => handleToggleTag("treatmentPlanning", tag)}
              >
                <FieldSuggestionDisplay
                  label="Rationale"
                  field={suggestions.suggestions.treatmentPlanning.rationale}
                />
              </CategorySuggestionSection>

              <CategorySuggestionSection
                title="Differential Diagnosis"
                suggestion={suggestions.suggestions.differentialDiagnosis}
                acceptedTags={acceptedTags["differentialDiagnosis"]}
                onToggleTag={(tag) =>
                  handleToggleTag("differentialDiagnosis", tag)
                }
              >
                <FieldSuggestionDisplay
                  label="Findings"
                  field={suggestions.suggestions.differentialDiagnosis.findings}
                />
              </CategorySuggestionSection>

              {/* Summary */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong className="text-slate-700">{totalAccepted}</strong>{" "}
                  suggestion(s) selected to apply. Review the selections above,
                  then click "Apply" to populate Clinical Arguments.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {step === "input" && (
              <button
                onClick={handleAnalyze}
                disabled={!noteText.trim() || noteText.trim().length < 50}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-700 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Analyze Note
              </button>
            )}
            {step === "review" && (
              <>
                <button
                  onClick={() => setStep("input")}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleApply}
                  disabled={totalAccepted === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Apply {totalAccepted} Suggestion
                  {totalAccepted !== 1 ? "s" : ""}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FontSizeToggle({
  fontSize,
  setFontSize,
  callMode,
}: {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  callMode: boolean;
}) {
  const sizes: FontSize[] = ["small", "medium", "large"];
  const currentIndex = sizes.indexOf(fontSize);

  const decrease = () => {
    if (currentIndex > 0) {
      setFontSize(sizes[currentIndex - 1]);
    }
  };

  const increase = () => {
    if (currentIndex < sizes.length - 1) {
      setFontSize(sizes[currentIndex + 1]);
    }
  };

  return (
    <div
      className={`flex items-center gap-1 rounded-lg p-1 ${
        callMode ? "bg-slate-700/50" : "bg-slate-100"
      }`}
    >
      <button
        onClick={decrease}
        disabled={currentIndex === 0}
        className={`p-1.5 rounded transition-all ${
          currentIndex === 0
            ? "opacity-30 cursor-not-allowed"
            : callMode
            ? "hover:bg-slate-600 text-slate-300"
            : "hover:bg-slate-200 text-slate-600"
        }`}
        title="Decrease font size"
      >
        <IconMinus className="w-3.5 h-3.5" />
      </button>
      <span
        className={`text-xs font-medium px-1 min-w-[20px] text-center ${
          callMode ? "text-slate-400" : "text-slate-500"
        }`}
      >
        A
      </span>
      <button
        onClick={increase}
        disabled={currentIndex === sizes.length - 1}
        className={`p-1.5 rounded transition-all ${
          currentIndex === sizes.length - 1
            ? "opacity-30 cursor-not-allowed"
            : callMode
            ? "hover:bg-slate-600 text-slate-300"
            : "hover:bg-slate-200 text-slate-600"
        }`}
        title="Increase font size"
      >
        <IconPlus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

interface QuickJumpSection {
  id: string;
  label: string;
  icon:
    | "talking"
    | "criteria"
    | "evidence"
    | "quick"
    | "risk"
    | "appeal"
    | "objection";
}

function QuickJumpPills({
  sections,
  activeSection,
  onJump,
  callMode,
  hasObjections,
}: {
  sections: ParsedSection[];
  activeSection: string | null;
  onJump: (sectionId: string) => void;
  callMode: boolean;
  hasObjections: boolean;
}) {
  const jumpSections: QuickJumpSection[] = useMemo(() => {
    const targets: QuickJumpSection[] = [];
    const seenTypes = new Set<string>();

    sections.forEach((section, index) => {
      let label = "";
      if (section.iconType === "talking") label = "Opening";
      else if (section.iconType === "criteria") label = "Criteria";
      else if (section.iconType === "evidence") label = "Evidence";
      else if (section.iconType === "quick") label = "Quick Ref";
      else if (section.iconType === "risk") label = "Risk";
      else if (section.iconType === "appeal") label = "Appeal";

      if (label && !seenTypes.has(section.iconType)) {
        seenTypes.add(section.iconType);
        targets.push({
          id: `section-${index}`,
          label,
          icon: section.iconType,
        });
      }
    });

    // Add Clinical Timeline chip near the front
    targets.splice(1, 0, {
      id: "section-timeline_builder",
      label: "Timeline",
      icon: "talking" as any, // Rendered with custom icon below
    });

    if (hasObjections) {
      targets.push({
        id: "section-rebuttals_decision_tree",
        label: "Rebuttals",
        icon: "objection",
      });
    }

    return targets;
  }, [sections, hasObjections]);

  if (jumpSections.length === 0) return null;

  return (
    <div
      className={`flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide ${
        callMode ? "text-slate-400" : "text-slate-500"
      }`}
    >
      <span className="text-xs font-medium uppercase tracking-wide whitespace-nowrap mr-1">
        Jump to:
      </span>
      {jumpSections.map((section) => (
        <button
          key={section.id}
          onClick={() => onJump(section.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
            activeSection === section.id
              ? callMode
                ? "bg-teal-500/20 text-teal-300 ring-1 ring-teal-500/30"
                : "bg-teal-50 text-teal-700 ring-1 ring-teal-200"
              : callMode
              ? "bg-slate-700/50 hover:bg-slate-700 text-slate-300"
              : "bg-slate-100 hover:bg-slate-200 text-slate-600"
          }`}
        >
          <SectionIcon type={section.icon} className="w-3.5 h-3.5" />
          {section.label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// UNIFIED SECTION ICON STYLING
// ============================================
// Professional, muted color for all section icons
// Uses a desaturated slate-teal that feels clinical and calm
const SECTION_ICON_COLOR = {
  light: "text-slate-500", // #64748b - muted slate for light mode
  dark: "text-slate-400", // #94a3b8 - slightly lighter for dark mode
};

// Unified content background - subtle, professional
const SECTION_CONTENT_BG = {
  light: "from-slate-50/40 to-transparent",
  dark: "from-slate-800/20 to-transparent",
};

function getSectionStyles(
  iconType: string,
  callMode: boolean
): { bgGradient: string; iconBg: string } {
  // All sections now use the same unified styling
  // No more semantic colors per section type
  return {
    bgGradient: callMode ? SECTION_CONTENT_BG.dark : SECTION_CONTENT_BG.light,
    iconBg: callMode ? SECTION_ICON_COLOR.dark : SECTION_ICON_COLOR.light,
  };
}

function SkeletonLoader() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="h-6 w-24 bg-slate-200 rounded animate-pulse" />
          <div className="h-8 w-24 bg-slate-100 rounded animate-pulse" />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-8 pb-8 border-b border-slate-200">
          <div className="h-4 w-12 bg-slate-200 rounded animate-pulse mb-2" />
          <div className="h-7 w-48 bg-slate-200 rounded animate-pulse mb-6" />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-5 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-slate-200 p-5"
            >
              <div className="h-5 w-40 bg-slate-100 rounded animate-pulse mb-4" />
              <div className="space-y-2">
                <div className="h-4 bg-slate-100 rounded animate-pulse" />
                <div className="h-4 bg-slate-100 rounded animate-pulse w-5/6" />
                <div className="h-4 bg-slate-100 rounded animate-pulse w-4/6" />
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

// ============================================
// CASE CONTEXT CLEANING
// Extracts duration, cleans shorthand, splits findings
// ============================================

function cleanRawCaseText(text: string): string {
  return text
    .replace(/\s*---\s*facility:\s*[^\n.;]*/gi, "") // remove "--- facility: office/clinic"
    .replace(/\bx\s*(\d+)\s*(weeks?|wks?|months?|mos?|days?)/gi, "$1 $2") // "x6 weeks" → "6 weeks"
    .replace(/\bw\/o\b/gi, "without")
    .replace(/\bw\/\b/gi, "with")
    .replace(/\bs\/p\b/gi, "status post")
    .replace(/\bh\/o\b/gi, "history of")
    .replace(/\bwks?\b/gi, "weeks")
    .replace(/\bmos?\b/gi, "months")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function extractDurationFromText(text: string): string | undefined {
  if (!text) return undefined;
  const patterns = [
    /[x×]\s*(\d+)\s*(weeks?|wks?|months?|mos?|days?|years?|yrs?)/i,
    /(?:for|over|lasting|persisting)\s+(\d+)\s*(weeks?|wks?|months?|mos?|days?|years?|yrs?)/i,
    /(\d+)\s*(weeks?|wks?|months?|mos?|days?|years?|yrs?)\s+(?:of|duration|history)/i,
    /(\d+)\s*(weeks?|wks?|months?|mos?)\s+(?:w\/o|without|despite|no)\s+improvement/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const num = match[1];
      let unit = match[2].toLowerCase();
      if (unit.startsWith("wk")) unit = "weeks";
      else if (unit.startsWith("mo")) unit = "months";
      else if (unit.startsWith("yr")) unit = "years";
      else if (!unit.endsWith("s") && num !== "1") unit += "s";
      return `${num} ${unit}`;
    }
  }
  return undefined;
}

function buildCleanKeyFindings(
  clinicalHistory: string | null | undefined,
  additionalContext: string | null | undefined
): string[] {
  const findings: string[] = [];
  const seen = new Set<string>();

  for (const raw of [clinicalHistory, additionalContext]) {
    if (!raw) continue;
    const cleaned = cleanRawCaseText(raw);
    // Split on commas, semicolons, or newlines
    const parts = cleaned
      .split(/[;,\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 4);
    for (const part of parts) {
      // Skip facility/admin fragments and pure duration fragments
      const lower = part.toLowerCase();
      if (lower.includes("facility:") || lower.includes("site of care"))
        continue;
      if (/^\d+\s*(weeks?|months?|days?)\s*(without|of|duration)/i.test(part))
        continue;
      const norm = lower.replace(/\s+/g, " ");
      if (!seen.has(norm) && findings.length < 5) {
        seen.add(norm);
        findings.push(part);
      }
    }
  }

  return findings;
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function CaseView() {
  const { id } = useParams();
  const { isPro } = useSubscription();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callMode, setCallMode] = useState(false);
  const [showPostCallRecap, setShowPostCallRecap] = useState(false);
  const [callSessionActive, setCallSessionActive] = useState(false);
  const [reviewerAuthority, setReviewerAuthority] =
    useState<ReviewerAuthority>("pending");
  const [reviewerId, setReviewerId] = useState("");
  const [callReferenceNumber, setCallReferenceNumber] = useState("");
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [fontSize, setFontSize] = useState<FontSize>("medium");
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [timelineOutput, setTimelineOutput] = useState<TimelineOutput | null>(
    null
  );
  const [showDenialModal, setShowDenialModal] = useState(false);
  const [triggerAppeal, setTriggerAppeal] = useState(false);
  const [clinicalArgument, setClinicalArgument] = useState<{
    clinical_justification: string;
    criteria_citations: string[];
    literature_references: string[];
    risks_of_denial: string[];
    recommended_additions: string[];
  } | null>(null);

  // ============================================
  // Case status lifecycle handlers
  // ============================================

  const handleStatusChange = (
    newStatus: string,
    extras?: Record<string, any>
  ) => {
    setCaseData((prev) =>
      prev ? { ...prev, case_status: newStatus, ...extras } : prev
    );
  };

  const handleDenialSaved = (denialReason: string, denialCode?: string) => {
    setCaseData((prev) =>
      prev
        ? {
            ...prev,
            case_status: "denied",
            denial_reason: denialReason,
            denial_code: denialCode || prev.denial_code,
            decision_at: new Date().toISOString(),
          }
        : prev
    );
    setShowDenialModal(false);
  };

  // Appeal flow: triggered from CaseStatusControls → PriorAuthLetterPanel
  const handleGenerateAppeal = useCallback(() => {
    setTriggerAppeal(true);
  }, []);

  const handleAppealGenerated = useCallback(async () => {
    // Update case status to "appealed"
    try {
      await supabase
        .from("cases")
        .update({ case_status: "appealed" })
        .eq("id", caseData?.id);

      setCaseData((prev) =>
        prev ? { ...prev, case_status: "appealed" } : prev
      );
    } catch (e) {
      console.error("[Appeal] Status update failed:", e);
    }
    setTriggerAppeal(false);
  }, [caseData?.id]);

  // P2P bridge: receive clinical argument from PriorAuthLetterPanel
  const handleClinicalArgumentReady = useCallback(
    (arg: {
      clinical_justification: string;
      criteria_citations: string[];
      literature_references: string[];
      risks_of_denial: string[];
      recommended_additions: string[];
    }) => {
      setClinicalArgument(arg);
    },
    []
  );

  // Star toggle (optimistic)
  const handleStar = async () => {
    if (!caseData) return;
    const newStarred = !caseData.is_starred;

    // Optimistic update
    setCaseData((prev) => (prev ? { ...prev, is_starred: newStarred } : prev));

    const { error } = await supabase
      .from("cases")
      .update({ is_starred: newStarred })
      .eq("id", caseData.id);

    if (error) {
      console.error("Failed to update star:", error);
      // Revert on failure
      setCaseData((prev) =>
        prev ? { ...prev, is_starred: !newStarred } : prev
      );
    }
  };

  // ============================================
  // Post-call recap handlers
  // ============================================

  // Tracks whether to exit call mode after recap dismisses
  const exitAfterRecapRef = useRef(false);

  /** Called by CallTranscription when listening successfully starts */
  const handleCallStarted = useCallback(() => {
    setCallSessionActive(true);
  }, []);

  /** Called by CallTranscription's stop button when call ends */
  const handleCallEnded = useCallback(() => {
    exitAfterRecapRef.current = false; // Stay in call mode (appeal letter screen)
    setShowPostCallRecap(true);
  }, []);

  /** Called when recap panel is closed (click outside, ESC, Skip).
   *  Hides panel but preserves form state for reopening. */
  const handleRecapClose = useCallback(() => {
    setShowPostCallRecap(false);
    // Don't reset callSessionActive — panel can reopen with state intact
  }, []);

  /** Called when physician clicks "Proceed" in the interpretation panel.
   *  Full dismiss: reset session, optionally exit call mode. */
  const handleRecapComplete = useCallback(() => {
    setShowPostCallRecap(false);
    setCallSessionActive(false);
    setReviewerAuthority("pending");
    setReviewerId("");
    setCallReferenceNumber("");
    reviewerIdCapturedRef.current = false;
    reviewerIdCollectingRef.current = false;
    reviewerIdDigitsRef.current = "";
    if (exitAfterRecapRef.current) {
      setCallMode(false);
      exitAfterRecapRef.current = false;
    }
  }, []);

  /** Intercepts "Exit Call Mode" button — shows recap if a call happened */
  const handleCallModeToggle = useCallback(() => {
    if (callMode) {
      // Exiting call mode
      if (callSessionActive) {
        // A call session occurred — show recap, then exit
        exitAfterRecapRef.current = true;
        setShowPostCallRecap(true);
      } else {
        // No call happened (just browsing prep) — exit directly
        setCallMode(false);
      }
    } else {
      // Entering call mode
      setCallMode(true);
    }
  }, [callMode, callSessionActive]);

  // Collapsible sections state

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState<Set<SectionKey>>(
    new Set(DEFAULT_EXPANDED_SECTIONS)
  );

  // Load expanded state from localStorage when case ID is available
  useEffect(() => {
    if (id) {
      const savedState = loadExpandedState(id);
      setExpandedSections(savedState);
    }
  }, [id]);

  // Save expanded state to localStorage whenever it changes
  const handleToggleSection = useCallback(
    (key: SectionKey) => {
      setExpandedSections((prev) => {
        const newState = new Set(prev);
        if (newState.has(key)) {
          newState.delete(key);
        } else {
          newState.add(key);
        }
        if (id) {
          saveExpandedState(id, newState);
        }
        return newState;
      });
    },
    [id]
  );

  const handleExpandAll = useCallback(() => {
    const newState = new Set<SectionKey>(ALL_SECTION_KEYS);
    setExpandedSections(newState);
    if (id) {
      saveExpandedState(id, newState);
    }
  }, [id]);

  const handleCollapseAll = useCallback(() => {
    const newState = new Set<SectionKey>();
    setExpandedSections(newState);
    if (id) {
      saveExpandedState(id, newState);
    }
  }, [id]);

  const handleResetSections = useCallback(() => {
    const newState = new Set<SectionKey>(DEFAULT_EXPANDED_SECTIONS);
    setExpandedSections(newState);
    if (id) {
      saveExpandedState(id, newState);
    }
  }, [id]);

  // Refs for section scrolling
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const adaptedCase = useMemo(
    () => (caseData ? adaptCaseForComponents(caseData) : null),
    [caseData]
  );

  // Build cleaned caseContext for DecisionTreeRebuttals
  const cleanedCaseContext = useMemo(() => {
    if (!caseData) return undefined;
    const allText = [caseData.clinical_history, caseData.additional_context]
      .filter(Boolean)
      .join(" ");

    // Include clinical argument from prior auth letter if available
    const priorAuthFindings = clinicalArgument
      ? [
          clinicalArgument.clinical_justification,
          ...clinicalArgument.criteria_citations,
        ].join("; ")
      : "";

    const combinedFindings = buildCleanKeyFindings(
      caseData.clinical_history,
      caseData.additional_context
    );
    const allFindings = priorAuthFindings
      ? [
          ...combinedFindings,
          ...priorAuthFindings.split(". ").filter((s) => s.length > 10),
        ]
      : combinedFindings;

    return {
      serviceName: caseData.request_details || "",
      serviceType: undefined as
        | "imaging"
        | "procedure"
        | "medication"
        | "dme"
        | "level_of_care"
        | "therapy"
        | "other"
        | undefined,
      clinicalProblem: caseData.diagnoses || "",
      patientAge: caseData.age_range || undefined,
      patientSex: caseData.sex || undefined,
      duration: extractDurationFromText(allText),
      keyFindings: allFindings,
      failedTreatments: adaptedCase?.tried_treatments || [],
      redFlags: caseData.comorbidities ? [caseData.comorbidities] : [],
      denialReason: caseData.denial_reason || undefined,
      insurerName: caseData.insurance_company || undefined,
    };
  }, [caseData, adaptedCase, clinicalArgument]);

  // Parse site-of-care info from additional_context
  const siteOfCareInfo = useMemo(
    () => parseSiteOfCareInfo(caseData?.additional_context),
    [caseData?.additional_context]
  );

  // Check if this is a site-of-care denial
  const isSiteOfCare = useMemo(() => isSiteOfCareDenial(caseData), [caseData]);

  // Compute "The Ask" for Call Scripts — one-sentence opening statement
  const theAsk = useMemo(() => {
    if (!caseData?.id) return "";
    const clinicalArgs = loadClinicalArgsFromStorage(caseData.id);
    const quickRef = extractQuickRefData(caseData, clinicalArgs);
    return quickRef.theAsk;
  }, [caseData]);

  // Initialize the tactical nudge system (replaces rebuttal engine)
  const {
    activeNudge,
    nudgeHistory,
    totalFired: nudgesFired,
    processTranscript: processNudgeTranscript,
    triggerManualNudge,
    setCaseContext: setNudgeCaseContext,
    startCall: startNudgeCall,
    dismissNudge,
    reset: resetNudge,
  } = useCallNudge({
    enabled: callMode,
    maxNudgesPerCall: 4,
    cooldownMs: 30000,
    autoDismissMs: 8000,
  });

  // Set case context for nudge engine when case data loads
  useEffect(() => {
    if (caseData) {
      setNudgeCaseContext({
        request_type: caseData.request_type,
        request_details: caseData.request_details,
        diagnoses: caseData.diagnoses,
        denial_reason: caseData.denial_reason,
        insurance_company: caseData.insurance_company,
        tried_already: caseData.tried_already,
      });
    }
  }, [caseData, setNudgeCaseContext]);

  // Transcript handler — feeds text to nudge engine + extracts reviewer ID
  //
  // Reviewer ID extraction — dead simple state machine:
  //   1. See trigger word ("reviewer", "reference", etc.) → start collecting
  //   2. Grab all digit characters from every token
  //   3. Stop when a token is purely alphabetic (a-z only, no digits)
  //      and is NOT a filler word ("is", "number", etc.)
  //
  const reviewerIdCapturedRef = useRef(false);
  const reviewerIdCollectingRef = useRef(false);
  const reviewerIdDigitsRef = useRef("");

  const processTranscript = useCallback(
    (text: string, speaker: string, conversationContext?: string) => {
      processNudgeTranscript(text);

      if (reviewerIdCapturedRef.current) return;

      // Normalize spoken number words to digits
      let chunk = text
        .replace(/\b(zero|oh)\b/gi, "0")
        .replace(/\bone\b/gi, "1")
        .replace(/\btwo\b/gi, "2")
        .replace(/\bthree\b/gi, "3")
        .replace(/\bfour\b/gi, "4")
        .replace(/\bfive\b/gi, "5")
        .replace(/\bsix\b/gi, "6")
        .replace(/\bseven\b/gi, "7")
        .replace(/\b(eight|ate)\b/gi, "8")
        .replace(/\bnine\b/gi, "9");

      const tokens = chunk.trim().split(/\s+/).filter(Boolean);

      for (const token of tokens) {
        const hasDigit = /\d/.test(token);
        const isAlphaOnly = /^[a-zA-Z']+$/.test(token);

        if (!reviewerIdCollectingRef.current) {
          // ── IDLE: looking for trigger word ──
          if (/^(?:reviewer|review|reference|ref|confirmation)$/i.test(token)) {
            reviewerIdCollectingRef.current = true;
            reviewerIdDigitsRef.current = "";
          }
        } else {
          // ── COLLECTING: grab digits or stop ──
          if (hasDigit) {
            // Token has digits — extract them all
            reviewerIdDigitsRef.current += token.replace(/[^0-9]/g, "");
          } else if (/^(ate|niner)$/i.test(token)) {
            // Unambiguous number homophones Deepgram might output
            const homoMap: Record<string, string> = { ate: "8", niner: "9" };
            reviewerIdDigitsRef.current += homoMap[token.toLowerCase()] || "";
          } else if (
            isAlphaOnly &&
            !/^(?:number|num|no|id|code|is|my|the|a|an|i'm|this|that|it's|hash|pound)$/i.test(
              token
            )
          ) {
            // Pure alphabetic word that isn't filler — the number is done
            if (reviewerIdDigitsRef.current.length >= 3) {
              setReviewerId(reviewerIdDigitsRef.current);
              reviewerIdCapturedRef.current = true;
              return;
            }
            // Too few digits — false trigger, reset
            reviewerIdCollectingRef.current = false;
            reviewerIdDigitsRef.current = "";
          }
          // Anything else (punctuation, filler words) — keep collecting
        }
      }
    },
    [processNudgeTranscript]
  );

  // Derive set of nudge types already triggered this call (for quick-tap dim state)
  const tappedNudgeTypes = useMemo(
    () => new Set(nudgeHistory.map((n) => n.type)),
    [nudgeHistory]
  );

  useEffect(() => {
    async function fetchCase() {
      const { data, error } = await supabase
        .from("cases")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        setError("Failed to load case");
        console.error(error);
      } else {
        setCaseData(data);
      }
      setLoading(false);
    }

    fetchCase();
  }, [id]);

  const handleJumpToSection = (sectionId: string) => {
    setActiveSection(sectionId);

    // Extract section key from sectionId and expand it
    const keyMatch = sectionId.match(/^section-(.+)$/);
    if (keyMatch) {
      const key = keyMatch[1] as SectionKey;
      if (ALL_SECTION_KEYS.includes(key) && !expandedSections.has(key)) {
        handleToggleSection(key);
      }
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const generatePrep = async () => {
    if (!caseData) return;

    setGenerating(true);
    setError(null);

    try {
      // Enrich caseData with clinical argument from prior auth letter (P2P bridge)
      const enrichedCaseData = { ...caseData };
      if (clinicalArgument) {
        const priorAuthContext = [
          "--- PRIOR AUTHORIZATION CLINICAL ARGUMENT (AI-generated) ---",
          clinicalArgument.clinical_justification,
          clinicalArgument.criteria_citations.length > 0
            ? `\nCriteria met: ${clinicalArgument.criteria_citations.join(
                "; "
              )}`
            : "",
          clinicalArgument.literature_references.length > 0
            ? `\nSupporting references: ${clinicalArgument.literature_references.join(
                "; "
              )}`
            : "",
        ]
          .filter(Boolean)
          .join("\n");

        enrichedCaseData.additional_context =
          enrichedCaseData.additional_context
            ? `${enrichedCaseData.additional_context}\n\n${priorAuthContext}`
            : priorAuthContext;
      }

      const response = await supabase.functions.invoke("generate-prep", {
        body: { caseData: enrichedCaseData },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const { prep } = response.data;

      const { error: updateError } = await supabase
        .from("cases")
        .update({ ai_prep: prep })
        .eq("id", caseData.id);

      if (updateError) {
        console.error("Failed to save prep:", updateError);
      }

      setCaseData({ ...caseData, ai_prep: prep });
      setCheckedItems(new Set());
    } catch (err) {
      console.error("Error generating prep:", err);
      setError("Failed to generate prep. Please try again.");
    }

    setGenerating(false);
  };

  const toggleChecked = (item: string) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(item)) {
      newChecked.delete(item);
    } else {
      newChecked.add(item);
    }
    setCheckedItems(newChecked);
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error && !caseData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/" className="text-teal-600 hover:text-teal-700">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!caseData) return null;

  const { sections, objectionPairs } = caseData.ai_prep
    ? parseAIPrep(caseData.ai_prep)
    : { sections: [], objectionPairs: [] };

  // Classify each section into a render category matching the render loop logic.
  // Categories that should only appear once are deduped (keep first occurrence).
  const getRenderCategory = (s: ParsedSection): string => {
    const upper = s.title.toUpperCase();
    const key = getSectionKey(s.title, s.iconType);
    // "CALL SCRIPTS" gets priority; "OPENING STATEMENT" is a separate fallback category
    if (upper.includes("CALL SCRIPT") && !upper.includes("OPENING"))
      return "call_scripts";
    if (upper.includes("OPENING STATEMENT") || upper.includes("OPENING"))
      return "opening_statement";
    if (
      key === "key_talking_points" ||
      upper.includes("KEY TALKING POINTS") ||
      upper.includes("TALKING POINTS")
    )
      return "talking_points";
    if (
      s.iconType === "evidence" ||
      upper.includes("EVIDENCE") ||
      upper.includes("CITATION") ||
      upper.includes("GUIDELINE")
    )
      return "evidence";
    if (
      key === "risk_if_denied" ||
      s.iconType === "risk" ||
      (upper.includes("RISK") && upper.includes("DENIED"))
    )
      return "risk_if_denied";
    // Default: use normalized title as category
    return upper.trim();
  };

  const callModeSectionTypes = ["talking", "risk"]; // Removed "quick" - rendered separately
  const callModeSections = (() => {
    const seen = new Set<string>();
    const deduped = sections
      .filter(
        (s) =>
          callModeSectionTypes.includes(s.iconType) ||
          s.title.toUpperCase().includes("OPENING") ||
          s.title.toUpperCase().includes("CALL SCRIPT") ||
          s.title.toUpperCase().includes("KEY TALKING")
      )
      .filter((s) => {
        const category = getRenderCategory(s);
        if (seen.has(category)) return false;
        seen.add(category);
        return true;
      });
    // Suppress OPENING STATEMENT when CALL SCRIPTS exists (same logic as filteredSections)
    const hasCallScripts = deduped.some(
      (s) => getRenderCategory(s) === "call_scripts"
    );
    if (hasCallScripts) {
      return deduped.filter(
        (s) => getRenderCategory(s) !== "opening_statement"
      );
    }
    return deduped;
  })();

  // Filter out legacy/separately-rendered sections, then deduplicate by render category
  const filteredSections = (() => {
    const seen = new Set<string>();
    const deduped = sections
      .filter(
        (s) =>
          s.iconType !== "criteria" &&
          s.iconType !== "quick" &&
          s.iconType !== "objection" &&
          !s.title.toUpperCase().includes("CRITERIA MAPPING") &&
          !s.title.toUpperCase().includes("RELEVANT CRITERIA") &&
          !s.title.toUpperCase().includes("QUICK REFERENCE") &&
          !s.title.toUpperCase().includes("ANTICIPATED OBJECTION") &&
          !s.title.toUpperCase().includes("REBUTTAL")
      )
      .filter((s) => {
        const category = getRenderCategory(s);
        if (seen.has(category)) return false;
        seen.add(category);
        return true;
      });
    // If we have a real CALL SCRIPTS section, drop the separate OPENING STATEMENT
    // (its content is redundant — the OPEN phase covers the same ground)
    const hasCallScripts = deduped.some(
      (s) => getRenderCategory(s) === "call_scripts"
    );
    if (hasCallScripts) {
      return deduped.filter(
        (s) => getRenderCategory(s) !== "opening_statement"
      );
    }
    return deduped;
  })();

  const displaySections = callMode ? callModeSections : filteredSections;
  const fontClass = getFontSizeClasses(fontSize, callMode);

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        callMode ? "bg-slate-900" : "bg-slate-50"
      }`}
    >
      {/* Document Checklist Panel */}
      {adaptedCase && (
        <DocumentChecklistPanel caseData={adaptedCase} callMode={callMode} />
      )}

      {/* Panic Button - only visible in Call Mode */}
      <PanicButton
        sections={sections}
        callMode={callMode}
        fontSize={fontSize}
      />

      {/* Header */}
      <header
        className={`${
          callMode
            ? "bg-slate-800/80 border-slate-700/50"
            : "bg-white/80 border-slate-200"
        } border-b sticky top-0 z-10 backdrop-blur-sm transition-colors duration-300`}
      >
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className={`text-lg font-semibold transition-colors ${
                  callMode ? "text-white" : "text-slate-900"
                }`}
              >
                <span className="text-teal-500">Peer</span>Prep
              </Link>
              <span
                className={`text-sm ${
                  callMode ? "text-slate-400" : "text-slate-500"
                }`}
              >
                {getCaseDisplayName(
                  caseData.request_details,
                  caseData.diagnoses
                )}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {caseData.ai_prep && (
                <FontSizeToggle
                  fontSize={fontSize}
                  setFontSize={setFontSize}
                  callMode={callMode}
                />
              )}

              {caseData.ai_prep && (
                <button
                  onClick={handleCallModeToggle}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                    callMode
                      ? "bg-teal-500 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {callMode ? "Exit Call Mode" : "Call Mode"}
                </button>
              )}
            </div>
          </div>

          {caseData.ai_prep && (
            <div className="mt-3 pt-3 border-t border-slate-200/50">
              <QuickJumpPills
                sections={sections}
                activeSection={activeSection}
                onJump={handleJumpToSection}
                callMode={callMode}
                hasObjections={objectionPairs.length > 0}
              />
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Case Summary - only in normal mode */}
        {!callMode && (
          <div className="mb-6">
            <CaseSummaryCard
              caseData={{
                ...caseData,
                insurance_company:
                  normalizeInsurer(caseData.insurance_company) ||
                  caseData.insurance_company,
              }}
              onStar={handleStar}
              onStatusChange={handleStatusChange}
              onDenialCapture={() => setShowDenialModal(true)}
              onGenerateAppeal={handleGenerateAppeal}
            />

            {/* Site of Care Display - NEW: Show when site-of-care info exists */}
            {(siteOfCareInfo.facilityLabel ||
              siteOfCareInfo.hospitalJustifications.length > 0) && (
              <div className="mt-4">
                <SiteOfCareDisplay
                  siteOfCareInfo={siteOfCareInfo}
                  callMode={false}
                  compact={false}
                />
              </div>
            )}

            {/* Prior Medications Panel */}
            <div className="mt-4">
              <MedicationPanel caseData={caseData} callMode={false} />
            </div>
          </div>
        )}

        {/* ━━━ Prior Authorization zone ━━━ */}
        {!callMode && (
          <div className="mb-6">
            <div className="rounded-lg overflow-hidden bg-white border border-slate-200 shadow-sm">
              {/* Group header */}
              <div className="px-5 py-4 flex items-center gap-3 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-50 border border-slate-200">
                  <IconDocument
                    className={`w-4 h-4 ${SECTION_ICON_COLOR.light}`}
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Prior Authorization
                  </h3>
                  <p className="text-xs text-teal-600">
                    Submission &amp; documentation
                  </p>
                </div>
              </div>

              {/* Items */}
              <div className="divide-y divide-slate-100">
                {/* Pre-submission Validation Checklist */}
                {caseData.case_status !== "approved" && (
                  <SubmissionChecklist
                    caseData={caseData}
                    onCaseUpdate={async (fields) => {
                      setCaseData((prev) =>
                        prev ? { ...prev, ...fields } : prev
                      );
                      try {
                        await supabase
                          .from("cases")
                          .update(fields)
                          .eq("id", caseData.id);
                      } catch (e) {
                        console.error("[CaseView] Checklist update failed:", e);
                      }
                    }}
                  />
                )}

                {/* Prior Auth Letter Generator */}
                <PriorAuthLetterPanel
                  caseData={caseData}
                  triggerAppeal={triggerAppeal}
                  onAppealGenerated={handleAppealGenerated}
                  onClinicalArgumentReady={handleClinicalArgumentReady}
                />

                {/* ICD-10 Code Reference Lookup */}
                <ICD10CodeLookup
                  currentDiagnoses={caseData.diagnoses}
                  onSelect={async (code, description) => {
                    // Skip if this code is already in the diagnoses
                    if (caseData.diagnoses?.includes(code)) return;

                    const newEntry = `${code} - ${description}`;
                    const existing = caseData.diagnoses?.trim();
                    const updated = existing
                      ? `${existing}, ${newEntry}`
                      : newEntry;

                    // Optimistic update
                    setCaseData((prev) =>
                      prev ? { ...prev, diagnoses: updated } : prev
                    );

                    // Persist
                    try {
                      await supabase
                        .from("cases")
                        .update({ diagnoses: updated })
                        .eq("id", caseData.id);
                    } catch (e) {
                      console.error(
                        "[CaseView] Failed to save diagnosis from lookup:",
                        e
                      );
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ━━━ P2P Call Preparation zone — only after denial ━━━ */}
        {!callMode &&
          (caseData.case_status === "denied" ||
            caseData.case_status === "appealed") && (
            <div className="mb-6">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                P2P Call Preparation
              </h2>
              <div className="divide-y divide-slate-100">
                {/* P2P Preparation Guide */}
                {!caseData.ai_prep && (
                  <div className="py-3">
                    <button
                      onClick={generatePrep}
                      disabled={generating}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left rounded-lg"
                    >
                      <div className="w-9 h-9 rounded-lg bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center flex-shrink-0">
                        <IconDocument className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-600">
                          {generating
                            ? "Generating..."
                            : "Generate P2P Preparation Guide"}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Call scripts, rebuttals, and insurer-specific talking
                          points
                        </p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Call Shield */}
                <div className="py-3">
                  {isPro ? (
                    <CallShieldToggle caseId={caseData.id} compact={callMode} />
                  ) : (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-400 text-sm font-medium w-full">
                      <IconLock className="w-4 h-4" />
                      Call Shield — Coming Soon
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* Denial Capture Modal */}
        {showDenialModal && (
          <DenialCaptureModal
            caseId={caseData.id}
            onDenialSaved={handleDenialSaved}
            onClose={() => setShowDenialModal(false)}
          />
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {caseData.ai_prep && (
          <div>
            {/* CALL MODE LAYOUT: Transcription + Decision Tree stacked at top */}
            {callMode && (
              <div className="space-y-4 mb-6">
                {/* Compact Call Header */}
                <div className="flex items-center justify-between pb-2 border-b border-slate-700/50">
                  <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
                    Live Call Tools
                  </h2>
                  {/* Nudge system indicator */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-800/50 rounded-full">
                    <div className="w-1.5 h-1.5 bg-slate-500 rounded-full" />
                    <span className="text-[10px] text-slate-500 font-medium">
                      {nudgesFired > 0
                        ? `${nudgesFired} nudge${nudgesFired > 1 ? "s" : ""}`
                        : "Listening"}
                    </span>
                  </div>
                </div>

                {/* Site of Care Display - Compact in Call Mode */}
                {(siteOfCareInfo.facilityLabel ||
                  siteOfCareInfo.hospitalJustifications.length > 0) && (
                  <SiteOfCareDisplay
                    siteOfCareInfo={siteOfCareInfo}
                    callMode={true}
                    compact={true}
                  />
                )}

                {/* Call metadata — reviewer ID + reference # */}
                <div
                  className={`
                  rounded-xl px-3 py-2.5 transition-all duration-500 relative
                  ${
                    reviewerId && callReferenceNumber
                      ? "bg-slate-800/30 border border-slate-700/20 shadow-none"
                      : "bg-gradient-to-r from-amber-900/35 via-amber-950/25 to-amber-900/20 border border-amber-500/50 border-l-[3px] border-l-amber-400 shadow-[0_0_24px_-3px_rgba(245,158,11,0.3),0_0_10px_-2px_rgba(245,158,11,0.25)] ring-1 ring-amber-500/15"
                  }
                `}
                >
                  {/* Label — fades out once both filled */}
                  <div
                    className={`
                    flex items-center gap-1.5 mb-2 transition-opacity duration-500
                    ${
                      reviewerId && callReferenceNumber
                        ? "opacity-0 h-0 mb-0 overflow-hidden"
                        : "opacity-100"
                    }
                  `}
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">
                      Log during call
                    </span>
                    <span className="text-[10px] text-amber-500/60">
                      — strengthens appeals
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Reviewer ID */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div
                        className={`
                        flex-shrink-0 w-5 h-5 rounded flex items-center justify-center
                        ${reviewerId ? "text-teal-400" : "text-amber-400/60"}
                      `}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 12 12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        >
                          <circle cx="6" cy="4" r="2.5" />
                          <path d="M1.5 11c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={reviewerId}
                        onChange={(e) => {
                          setReviewerId(e.target.value);
                          reviewerIdCollectingRef.current = false;
                          reviewerIdDigitsRef.current = "";
                          if (e.target.value.trim())
                            reviewerIdCapturedRef.current = true;
                          else reviewerIdCapturedRef.current = false;
                        }}
                        placeholder="Reviewer ID"
                        className={`
                          w-full bg-transparent border-none outline-none text-sm
                          placeholder-amber-400/50 transition-colors
                          ${
                            reviewerId
                              ? "text-teal-200 font-mono tracking-wide"
                              : "text-slate-300"
                          }
                        `}
                      />
                      {reviewerId && (
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400/80 flex-shrink-0" />
                      )}
                    </div>

                    {/* Divider */}
                    <div className="w-px h-5 bg-amber-500/30 flex-shrink-0" />

                    {/* Call Reference # */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div
                        className={`
                        flex-shrink-0 w-5 h-5 rounded flex items-center justify-center
                        ${
                          callReferenceNumber
                            ? "text-teal-400"
                            : "text-amber-400/60"
                        }
                      `}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 12 12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        >
                          <path d="M2 3h8M2 6h8M2 9h5" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={callReferenceNumber}
                        onChange={(e) => setCallReferenceNumber(e.target.value)}
                        placeholder="Call ref #"
                        className={`
                          w-full bg-transparent border-none outline-none text-sm
                          placeholder-amber-400/50 transition-colors
                          ${
                            callReferenceNumber
                              ? "text-teal-200 font-mono tracking-wide"
                              : "text-slate-300"
                          }
                        `}
                      />
                      {callReferenceNumber && (
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400/80 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Call Consult */}
                <CallTranscription
                  callMode={callMode}
                  caseData={caseData}
                  onProcessTranscript={processTranscript}
                  onCallStarted={handleCallStarted}
                  onCallEnded={handleCallEnded}
                  statusBarSlot={
                    callSessionActive ? (
                      <ReviewerAuthorityStrip
                        status={reviewerAuthority}
                        onAnswer={setReviewerAuthority}
                      />
                    ) : undefined
                  }
                  bottomSlot={
                    callSessionActive ? (
                      <CallQuickTap
                        onTap={triggerManualNudge}
                        tappedTypes={tappedNudgeTypes}
                      />
                    ) : undefined
                  }
                />

                {/* Medication Arguments - Call Mode */}
                <MedicationPanel caseData={caseData} callMode={true} />
              </div>
            )}

            {/* Section Controls Header — prep mode only */}
            {!callMode && (
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-sm font-medium uppercase tracking-wide text-slate-400">
                  Preparation Guide
                </h2>
                <div className="flex items-center gap-4">
                  <SectionControls
                    expandedSections={expandedSections}
                    onExpandAll={handleExpandAll}
                    onCollapseAll={handleCollapseAll}
                    onReset={handleResetSections}
                    callMode={callMode}
                  />
                  <button
                    onClick={generatePrep}
                    disabled={generating}
                    className="text-sm transition-colors text-slate-400 hover:text-teal-600"
                  >
                    {generating ? "Regenerating..." : "Regenerate"}
                  </button>
                </div>
              </div>
            )}

            {/* Clinical Timeline (Prep Mode only) */}
            {!callMode && id && (
              <div id="section-timeline_builder" className="mb-6 scroll-mt-32">
                <TimelineBuilder
                  caseId={id}
                  caseType={getTimelineCaseType(caseData?.request_type) as any}
                  caseData={{
                    tried_treatments: adaptedCase?.tried_treatments,
                    clinical_history: caseData?.clinical_history,
                    diagnoses: caseData?.diagnoses,
                    denial_reason: caseData?.denial_reason,
                    request_type: caseData?.request_type,
                    request_details: caseData?.request_details,
                    comorbidities: caseData?.comorbidities,
                  }}
                  onTimelineChange={(timeline) => {
                    console.log("[Timeline] Changed:", timeline);
                  }}
                  onGenerateOutput={(output) => {
                    setTimelineOutput(output);
                    console.log("[Timeline] Generated output:", output);
                  }}
                />
              </div>
            )}

            {/* Call Playbook — grouped container */}
            <div
              className={`rounded-lg overflow-hidden ${
                callMode
                  ? "bg-slate-800/20 border border-slate-700/30"
                  : "bg-white border border-slate-200 shadow-sm"
              }`}
            >
              {/* Group header */}
              <div
                className={`px-5 py-4 flex items-center gap-3 border-b ${
                  callMode ? "border-slate-700/30" : "border-slate-100"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    callMode
                      ? "bg-slate-700/50"
                      : "bg-slate-50 border border-slate-200"
                  }`}
                >
                  <IconBolt
                    className={`w-4 h-4 ${
                      callMode
                        ? SECTION_ICON_COLOR.dark
                        : SECTION_ICON_COLOR.light
                    }`}
                  />
                </div>
                <div>
                  <h3
                    className={`text-sm font-semibold ${
                      callMode ? "text-slate-200" : "text-slate-800"
                    }`}
                  >
                    Call Playbook
                  </h3>
                  <p
                    className={`text-xs ${
                      callMode ? "text-slate-500" : "text-teal-600"
                    }`}
                  >
                    Your call strategy
                  </p>
                </div>
              </div>
              {displaySections.map((section, index) => {
                const sectionStyles = getSectionStyles(
                  section.iconType,
                  callMode
                );
                const sectionKey = getSectionKey(
                  section.title,
                  section.iconType
                );
                const isExpanded = sectionKey
                  ? expandedSections.has(sectionKey)
                  : true;
                const isLastItem = index === displaySections.length - 1;

                // For call mode, don't use collapsible (already filtered to essential sections)
                if (callMode) {
                  // Call Scripts card in call mode — check FIRST
                  const isCallScripts =
                    section.title.toUpperCase().includes("CALL SCRIPT") ||
                    section.title.toUpperCase().includes("OPENING STATEMENT");

                  if (isCallScripts) {
                    return (
                      <div
                        key={index}
                        id={`section-${index}`}
                        className="scroll-mt-32"
                      >
                        <CallScriptsCard
                          content={section.content}
                          theAsk={theAsk}
                          callMode={true}
                          inContainer={true}
                        />
                      </div>
                    );
                  }

                  // Special rendering for Risk if Denied in call mode
                  const isRiskSection =
                    sectionKey === "risk_if_denied" ||
                    section.iconType === "risk" ||
                    (section.title.toUpperCase().includes("RISK") &&
                      section.title.toUpperCase().includes("DENIED"));

                  if (isRiskSection) {
                    return (
                      <CollapsibleSection
                        key={index}
                        sectionKey={sectionKey || (("section_" + index) as any)}
                        title="Risk if Denied"
                        icon={
                          <SectionIcon
                            type="risk"
                            className={`w-3.5 h-3.5 ${sectionStyles.iconBg} opacity-90`}
                          />
                        }
                        isExpanded={
                          sectionKey ? expandedSections.has(sectionKey) : false
                        }
                        onToggle={sectionKey ? handleToggleSection : () => {}}
                        callMode={callMode}
                        headerActions={
                          <CopyButton
                            text={section.content}
                            callMode={callMode}
                          />
                        }
                        inContainer={true}
                      >
                        <RiskIfDeniedSection
                          content={section.content}
                          caseId={id || "temp"}
                          callMode={true}
                          caseData={caseData}
                        />
                      </CollapsibleSection>
                    );
                  }

                  return (
                    <CollapsibleSection
                      key={index}
                      sectionKey={sectionKey || (("section_" + index) as any)}
                      title={section.title}
                      icon={
                        <SectionIcon
                          type={section.iconType}
                          className={`w-3.5 h-3.5 ${sectionStyles.iconBg} opacity-90`}
                        />
                      }
                      isExpanded={
                        sectionKey ? expandedSections.has(sectionKey) : false
                      }
                      onToggle={sectionKey ? handleToggleSection : () => {}}
                      callMode={callMode}
                      headerActions={
                        <CopyButton
                          text={section.content}
                          callMode={callMode}
                        />
                      }
                      inContainer={true}
                    >
                      <div className={`${fontClass} text-slate-300`}>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <p className="mb-3 leading-relaxed">{children}</p>
                            ),
                            ul: ({ children }) => (
                              <ul className="space-y-2">{children}</ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="space-y-2">{children}</ol>
                            ),
                            li: ({ children }) => (
                              <li className="flex items-start gap-2">
                                <span className="text-teal-400 mt-1">•</span>
                                <span className="leading-relaxed">
                                  {children}
                                </span>
                              </li>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-semibold text-white">
                                {children}
                              </strong>
                            ),
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-4 pl-4 my-3 italic border-teal-500 text-slate-300 bg-slate-700/30 py-2 rounded-r">
                                {children}
                              </blockquote>
                            ),
                          }}
                        >
                          {section.content}
                        </ReactMarkdown>
                      </div>
                    </CollapsibleSection>
                  );
                }

                // Normal mode: use collapsible sections inside container
                // Special rendering for Call Scripts / Opening Statement
                const isCallScripts =
                  section.title.toUpperCase().includes("CALL SCRIPT") ||
                  section.title.toUpperCase().includes("OPENING STATEMENT");

                if (isCallScripts) {
                  return (
                    <div
                      key={index}
                      id={`section-${index}`}
                      className="scroll-mt-32"
                    >
                      <CallScriptsCard
                        content={section.content}
                        theAsk={theAsk}
                        callMode={callMode}
                        defaultCollapsed={true}
                        inContainer={true}
                      />
                    </div>
                  );
                }

                // Special rendering for Key Talking Points
                const isKeyTalkingPoints =
                  sectionKey === "key_talking_points" ||
                  section.title.toUpperCase().includes("KEY TALKING POINTS") ||
                  section.title.toUpperCase().includes("TALKING POINTS");

                if (isKeyTalkingPoints) {
                  return (
                    <CollapsibleSection
                      key={index}
                      sectionKey={sectionKey || (("section_" + index) as any)}
                      title={section.title}
                      icon={
                        <SectionIcon
                          type={section.iconType}
                          className={`w-3.5 h-3.5 ${sectionStyles.iconBg} opacity-90`}
                        />
                      }
                      isExpanded={isExpanded}
                      onToggle={sectionKey ? handleToggleSection : () => {}}
                      callMode={callMode}
                      headerActions={
                        <CopyButton
                          text={section.content}
                          callMode={callMode}
                        />
                      }
                      contentClassName={`bg-gradient-to-r ${sectionStyles.bgGradient}`}
                      inContainer={true}
                      isLastItem={isLastItem}
                    >
                      <KeyTalkingPointsSection
                        content={section.content}
                        caseId={id || "temp"}
                        callMode={callMode}
                        fontSize={fontSize}
                        caseData={caseData}
                      />
                    </CollapsibleSection>
                  );
                }

                // Special rendering for Evidence sections - professional clinical reference
                const isEvidenceSection =
                  section.iconType === "evidence" ||
                  section.title.toUpperCase().includes("EVIDENCE") ||
                  section.title.toUpperCase().includes("CITATION") ||
                  section.title.toUpperCase().includes("GUIDELINE");

                if (isEvidenceSection) {
                  return (
                    <CollapsibleSection
                      key={index}
                      sectionKey={sectionKey || (("section_" + index) as any)}
                      title="Evidence & Guideline Support"
                      icon={<IconBook className="w-3.5 h-3.5 text-slate-400" />}
                      isExpanded={isExpanded}
                      onToggle={sectionKey ? handleToggleSection : () => {}}
                      callMode={callMode}
                      headerActions={
                        <CopyButton
                          text={section.content}
                          callMode={callMode}
                        />
                      }
                      contentClassName=""
                      inContainer={true}
                      isLastItem={isLastItem}
                    >
                      <GuidelineSupport
                        content={section.content}
                        callMode={callMode}
                      />
                    </CollapsibleSection>
                  );
                }

                // Special rendering for Risk if Denied - consequence chips
                const isRiskSection =
                  sectionKey === "risk_if_denied" ||
                  section.iconType === "risk" ||
                  (section.title.toUpperCase().includes("RISK") &&
                    section.title.toUpperCase().includes("DENIED"));

                if (isRiskSection) {
                  return (
                    <CollapsibleSection
                      key={index}
                      sectionKey={sectionKey || (("section_" + index) as any)}
                      title="Risk if Denied"
                      icon={
                        <SectionIcon
                          type="risk"
                          className={`w-3.5 h-3.5 ${sectionStyles.iconBg} opacity-90`}
                        />
                      }
                      isExpanded={isExpanded}
                      onToggle={sectionKey ? handleToggleSection : () => {}}
                      callMode={callMode}
                      headerActions={
                        <CopyButton
                          text={section.content}
                          callMode={callMode}
                        />
                      }
                      contentClassName=""
                      inContainer={true}
                      isLastItem={isLastItem}
                    >
                      <RiskIfDeniedSection
                        content={section.content}
                        caseId={id || "temp"}
                        callMode={callMode}
                        caseData={caseData}
                      />
                    </CollapsibleSection>
                  );
                }

                // Default rendering for other sections
                return (
                  <CollapsibleSection
                    key={index}
                    sectionKey={sectionKey || (("section_" + index) as any)}
                    title={section.title}
                    icon={
                      <SectionIcon
                        type={section.iconType}
                        className={`w-3.5 h-3.5 ${sectionStyles.iconBg} opacity-90`}
                      />
                    }
                    isExpanded={isExpanded}
                    onToggle={sectionKey ? handleToggleSection : () => {}}
                    callMode={callMode}
                    headerActions={
                      <CopyButton text={section.content} callMode={callMode} />
                    }
                    contentClassName={`bg-gradient-to-r ${sectionStyles.bgGradient}`}
                    inContainer={true}
                    isLastItem={isLastItem}
                  >
                    <div
                      className={`${fontClass} ${
                        callMode ? "text-slate-300" : "text-slate-600"
                      }`}
                    >
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p className="mb-3 leading-relaxed">{children}</p>
                          ),
                          ul: ({ children }) => (
                            <ul className="space-y-2">{children}</ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="space-y-2">{children}</ol>
                          ),
                          li: ({ children }) => {
                            const text = String(children);
                            const isChecked = checkedItems.has(text);

                            return (
                              <li
                                className={`flex items-start gap-3 cursor-pointer group transition-all duration-150 ${
                                  isChecked ? "opacity-40" : ""
                                }`}
                                onClick={() => toggleChecked(text)}
                              >
                                <span
                                  className={`flex-shrink-0 w-4 h-4 mt-1 rounded border transition-all duration-150 ${
                                    isChecked
                                      ? "bg-teal-500 border-teal-500"
                                      : "border-slate-300 group-hover:border-teal-500"
                                  } flex items-center justify-center`}
                                >
                                  {isChecked && (
                                    <IconCheck className="w-3 h-3 text-white" />
                                  )}
                                </span>
                                <span className="leading-relaxed">
                                  {children}
                                </span>
                              </li>
                            );
                          },
                          strong: ({ children }) => (
                            <strong
                              className={`font-semibold ${
                                callMode ? "text-white" : "text-slate-900"
                              }`}
                            >
                              {children}
                            </strong>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote
                              className={`border-l-4 pl-4 my-3 italic ${
                                callMode
                                  ? "border-teal-500 text-slate-300 bg-slate-700/30"
                                  : "border-teal-500 text-slate-600 bg-teal-50/50"
                              } py-2 rounded-r`}
                            >
                              {children}
                            </blockquote>
                          ),
                        }}
                      >
                        {section.content}
                      </ReactMarkdown>
                    </div>
                  </CollapsibleSection>
                );
              })}
            </div>

            {/* Insurer Intel — grouped container matching Call Scripts pattern */}
            {!callMode &&
              (adaptedCase ||
                objectionPairs.length > 0 ||
                sections.some((s) => s.iconType === "objection")) && (
                <div
                  className={`mt-4 rounded-lg overflow-hidden ${
                    callMode
                      ? "bg-slate-800/20 border border-slate-700/30"
                      : "bg-white border border-slate-200 shadow-sm"
                  }`}
                >
                  {/* Group header */}
                  <div
                    className={`px-5 py-4 flex items-center gap-3 border-b ${
                      callMode ? "border-slate-700/30" : "border-slate-100"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        callMode
                          ? "bg-slate-700/50"
                          : "bg-slate-50 border border-slate-200"
                      }`}
                    >
                      <IconShield
                        className={`w-4 h-4 ${
                          callMode
                            ? SECTION_ICON_COLOR.dark
                            : SECTION_ICON_COLOR.light
                        }`}
                      />
                    </div>
                    <div>
                      <h3
                        className={`text-sm font-semibold ${
                          callMode ? "text-slate-200" : "text-slate-800"
                        }`}
                      >
                        Insurer Intel
                      </h3>
                      <p
                        className={`text-xs ${
                          callMode ? "text-slate-500" : "text-teal-600"
                        }`}
                      >
                        Know before you call
                      </p>
                    </div>
                  </div>

                  {/* Coverage Requirements */}
                  {adaptedCase && (
                    <CollapsibleSection
                      sectionKey="coverage_requirements"
                      title="Coverage Requirements"
                      icon={
                        <IconClipboard
                          className={`w-3.5 h-3.5 ${
                            callMode
                              ? SECTION_ICON_COLOR.dark
                              : SECTION_ICON_COLOR.light
                          } opacity-90`}
                        />
                      }
                      isExpanded={expandedSections.has("coverage_requirements")}
                      onToggle={handleToggleSection}
                      callMode={callMode}
                      inContainer={true}
                      isLastItem={
                        !objectionPairs.length &&
                        !sections.some((s) => s.iconType === "objection")
                      }
                    >
                      <CoverageRequirementsPanel
                        caseData={adaptedCase}
                        caseId={id || "temp"}
                        callMode={callMode}
                      />
                    </CollapsibleSection>
                  )}

                  {/* Decision Tree Rebuttals */}
                  {objectionPairs.length > 0 && (
                    <CollapsibleSection
                      sectionKey="rebuttals_decision_tree"
                      title="Most Likely Objections"
                      icon={
                        <IconShield
                          className={`w-3.5 h-3.5 ${
                            callMode
                              ? SECTION_ICON_COLOR.dark
                              : SECTION_ICON_COLOR.light
                          } opacity-90`}
                        />
                      }
                      isExpanded={expandedSections.has(
                        "rebuttals_decision_tree"
                      )}
                      onToggle={handleToggleSection}
                      callMode={callMode}
                      inContainer={true}
                      isLastItem={true}
                    >
                      <DecisionTreeRebuttals
                        objectionPairs={objectionPairs}
                        callMode={callMode}
                        fontSize={fontSize}
                        procedureName={caseData?.request_details}
                        caseContext={cleanedCaseContext!}
                      />
                    </CollapsibleSection>
                  )}

                  {/* Fallback for objection sections without parsed pairs */}
                  {objectionPairs.length === 0 &&
                    sections.some((s) => s.iconType === "objection") && (
                      <div
                        className={`p-5 ${
                          callMode ? "bg-slate-800/50" : "bg-white"
                        }`}
                      >
                        <h3
                          className={`text-sm font-semibold mb-4 flex items-center gap-2 ${
                            callMode ? "text-slate-300" : "text-slate-700"
                          }`}
                        >
                          <IconShield
                            className={`w-3.5 h-3.5 ${
                              callMode
                                ? SECTION_ICON_COLOR.dark
                                : SECTION_ICON_COLOR.light
                            } opacity-90`}
                          />
                          Anticipated Objections & Rebuttals
                        </h3>
                        <div
                          className={`${fontClass} ${
                            callMode ? "text-slate-300" : "text-slate-600"
                          }`}
                        >
                          <ReactMarkdown>
                            {sections.find((s) => s.iconType === "objection")
                              ?.content || ""}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}
                </div>
              )}
          </div>
        )}

        {!callMode && (
          <div className="mt-8 pt-8 border-t border-slate-200">
            <Link
              to="/"
              className="text-sm text-slate-400 hover:text-teal-600 transition-colors"
            >
              ← Back to cases
            </Link>
          </div>
        )}
      </main>

      {/* Tactical nudge overlay (whisper cues during calls) */}
      {callMode && (
        <CallNudgeOverlay nudge={activeNudge} onDismiss={dismissNudge} />
      )}

      {/* Post-Call Recap Panel (always mounted for state preservation) */}
      {caseData && (
        <PostCallRecap
          caseId={caseData.id}
          insuranceCompany={caseData.insurance_company}
          reviewerId={reviewerId}
          callReferenceNumber={callReferenceNumber}
          isOpen={showPostCallRecap}
          onClose={handleRecapClose}
          onComplete={handleRecapComplete}
        />
      )}
    </div>
  );
}
